// ============================================================================
// src/App.tsx — the application component.
// ============================================================================
// The root-only render chain (createRoot, ErrorBoundary, BrowserRouter, and
// AppProviders) lives in src/main.tsx. This component owns application state,
// compatibility routing, and released-surface rendering.
// ============================================================================

import {createConfirmedDraftAutosave} from './shared/confirmedDraftAutosave.js';
import React from 'react';
// B3.1 finish: the domain predicate + routed location every converted entry is
// dispatched with. Domains still on a resolver keep the B3.2 shape below until
// they are converted in turn.
import {routedLocationFor, useAppDraftGuard, useAppLocation, useAppNavigate} from './app/navigation/index.js';
import {appQueryClient} from './app/data/queryClient.js';
import {RESOURCE} from './app/data/routeDataCatalog.js';
import {AppEntry} from './app/AppEntry.tsx';
import {APP_ROUTES_PRE_FORM, APP_ROUTES_POST_FORM} from './app/appRoutes.js';
// Per-domain entries. Each owns the clauses that used to sit inline in the
// dispatch below.
import {CattleEntry} from './cattle/CattleEntry.jsx';
import {CATTLE_ROUTES} from './cattle/cattleRoutes.js';
import {SheepEntry} from './sheep/SheepEntry.jsx';
import {SHEEP_ROUTES} from './sheep/sheepRoutes.js';
import {LayerEntry} from './layer/LayerEntry.jsx';
import {LAYER_ROUTES} from './layer/layerRoutes.js';
import {BroilerEntry} from './broiler/BroilerEntry.jsx';
import {BROILER_ROUTES} from './broiler/broilerRoutes.js';
import {PigEntry} from './pig/PigEntry.jsx';
import {PIG_ROUTES} from './pig/pigRoutes.js';
import {WebformsEntry} from './webforms/WebformsEntry.jsx';
import {WEBFORMS_ROUTES} from './webforms/webformsRoutes.js';
// applyManifestHref is re-exported through the bootstrap module because App
// calls it from a useEffect keyed on location.pathname.
import {applyManifestHref} from './app/bootstrap/runtimeInit.js';
import {VIEW_TO_PATH, PATH_TO_VIEW, resolvePathAlias} from './lib/routes.js';
import {VIEW_TO_PROGRAM, isProcessingRole} from './lib/globalNav.js';
import {
  isInactiveAccount,
  resolveSurfaceAccess,
  SURFACE_NAVIGATION,
  visibleDailyHistoryPrograms,
  type AuthState as AccessAuthState,
} from './lib/access.js';
import {sb} from './lib/supabase.js';
import {loadAllPoultryDailys} from './broiler/poultryDailysLoader.js';
import {loadAllPigDailys} from './pig/pigDailysLoader.js';
import {
  createLegacyBootCoordinator,
  type LegacyBootCoordinator,
  type LegacyBootLease,
} from './app/boot/legacyBootCoordinator.ts';
// The canonical cut-sheet record route contract. The URL→view adapter below is
// the caller: `/processing/cut-sheet/<recordId>` has no PATH_TO_VIEW entry, so
// without an explicit branch it falls through to the unknown-path fallback and
// snaps to Home. Re-deriving the shape inline would give the one route two
// definitions that can drift; this module is the single fence.
import {isCanonicalCutSheetEditorPathname} from './app/boot/cutSheetRecordPaths.ts';
import {isCanonicalAdminEquipmentRecordPathname} from './app/boot/adminEquipmentRecordPath.ts';
import {
  claimReleasedSurfaceBoundary,
  isCurrentReleasedSurfaceClaim,
  RELEASED_BROILER_BATCHES_DETAIL_SURFACE,
  RELEASED_BROILER_BATCHES_INDEX_SURFACE,
  RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE,
  RELEASED_BROILER_TIMELINE_SURFACE,
  resolveAuthenticatedRouteDisposition,
  type ReleasedSurfaceClaim,
} from './app/boot/releasedSurfaceBoundary.ts';
import {
  ReleasedBroilerBatchesActionsProvider,
  ReleasedBroilerBatchFormActionsProvider,
  renderReleasedAuthenticatedSurface,
} from './app/boot/releasedSurfaceRenderer.tsx';
import {applyBroilerBootRepair} from './app/boot/broilerBootRepair.ts';
import {parseProcessorWorkbook} from './lib/processorXlsx.js';
import {PROFILE_STATUS, useAuth, type AuthIdentity, type AuthState} from './contexts/AuthContext.jsx';
import {useBatches} from './contexts/BatchesContext.jsx';
import {usePig} from './contexts/PigContext.jsx';
import {useLayer} from './contexts/LayerContext.jsx';
import {useDailysRecent} from './contexts/DailysRecentContext.jsx';
import {useFeedCosts} from './contexts/FeedCostsContext.jsx';
import {useUI} from './contexts/UIContext.jsx';
import {useConfirm} from './contexts/ConfirmContext.jsx';
import Header from './shared/Header.tsx';
import {invalidateCattleWeighInsCache} from './lib/cattleCache.js';
import {EMPTY_FORM} from './app/data/broilerFormDefaults.js';
import SetPasswordScreen from './auth/SetPasswordScreen.jsx';
import LoginScreen from './auth/LoginScreen.jsx';
import {
  LEGACY_BREEDS,
  LEGACY_HATCHERIES,
  calcTimeline,
  resolveBrooderEntryDate,
  calcPoultryStatus,
  hatcheryGrowoutWeeks,
  validBrooderDays,
  isInvalidBrooderDays,
} from './lib/broiler.js';
import {planBroilerBatchesSave, readFreshBroilerBatches, reportBroilerBatchesRefusal} from './lib/broilerBatchesApi.js';
import type {BroilerBatchDocumentsPatch, FreshBroilerBatchesRead} from './lib/broilerBatchesApi.js';
import {
  describeBroilerSaveFailure,
  planBroilerBatchOperations,
  runBroilerBatchOperations,
} from './lib/broilerBatchOperationApi.js';
import {readSessionProfile} from './lib/sessionProfileApi.js';
import {
  SESSION_PROFILE_ERROR_SOURCE,
  describeSessionProfileFailure,
  loadSessionProfile,
} from './app/boot/sessionProfileLoad.ts';
import {reportError} from './lib/clientErrorReporting.js';
import {createDeferredAuthIntake} from './app/boot/deferredAuthIntake.ts';
import {rememberAuthIdentity} from './lib/authIdentityMemory.js';
import {detectConflicts, type ConflictForm} from './lib/conflicts.js';
import NewsletterPublicApp from './newsletter/NewsletterPublicApp.tsx';
import LightHomePortal from './dashboard/LightHomePortal.tsx';
import InactiveAccountScreen from './shared/InactiveAccountScreen.tsx';
import BatchForm from './broiler/BatchForm.tsx';
import {titleForView} from './lib/pageTitles.js';
import {BootSplash, NoAccessCard, NotFoundCard} from './shared/PageStates.js';
import PageBoundary from './app/PageBoundary.tsx';
import {parentPathFor} from './lib/parentRoute.js';

// A URL hash carrying a Supabase auth result (recovery, invite, magic link,
// email change, or its error). Such a link must reach / with its hash intact.
const AUTH_LINK_HASH =
  /access_token=|refresh_token=|type=(?:recovery|invite|signup|magiclink|email_change)|error_description=|error_code=/;
import {AUTHENTICATED_SURFACE_FALLBACK} from './app/boot/authenticatedSurfaceResolver.js';

// ============================================================================
// WCF Planner — App
// ============================================================================
// SheetJS loading is owned by src/lib/processorXlsx.ts, including the
// `/* global XLSX */` declaration and window._wcfLoadXLSX lazy load.
// ============================================================================

// Navigation config owns VIEW_TO_PROGRAM (one authoritative program
// classification per view, shared with the global header) and the Processing
// role list, so neither is maintained twice.
// Boot steps that are not application logic. Call order is a contract: see the
// header of runtimeInit.js — initRuntime() before render, retireBootLoader()
// strictly after it.

// Shared helpers are imported into this application module. The pig and
// broiler daily loaders own their paginated reads in their respective modules.
// AuthContext owns auth state; App reads the consumer hooks while
// src/app/AppProviders.tsx mounts the provider halves.

// Phase 2.0.2: BatchesContext owns the broiler batch + edit-form hooks.
// EMPTY_FORM is passed in as the form initializer (from src/app/data/).

// Phase 2.0.3: PigContext owns all pig-scoped useState hooks. INITIAL_FARROWING,
// INITIAL_BREEDERS, and the breedTlStart lazy init are threaded in as props.

// Phase 2.0.4: LayerContext owns layer-scoped useState hooks.

// Phase 2.0.5: DailysRecentContext owns the recent-window dailys arrays
// across broiler/pig/layer/egg/cattle/sheep.

// Phase 2.0.6: five small contexts bundled in one commit.

// Phase 2.1.1: leaf form-input components extracted to src/shared/.

// Phase 2.1.2: DeleteModal extracted to src/shared/.

// Phase 2.1.3 prep: layer-housing helpers extracted ahead of modal extractions
// so the admin modal + webform hub can import them without a circular
// dependency on App.

// Phase 2.1.4: Admin + livestock + cattle modals extracted verbatim.

// Phase 2.1.5: SetPasswordScreen + LoginScreen extracted to src/auth/.

// Phase 2 Round 2: single-feature dailys views + cow/sheep detail + bulk imports.

// Phase 2.3.5/2.3.6 bug-fix: LivestockWeighInsView + CattleWeighInsView
// were accidentally bundled into adjacent modal files in Round 1. Split.

// Phase 2.3 prep: helpers needed by Round 3 views extracted to src/lib/.
// B2.1: DEFAULT_WEBFORMS_CONFIG now reaches WebformsConfigProvider from
// src/app/AppProviders.tsx, which is the only place that mounted it.
// Broiler helpers live in src/lib/broiler.ts so BroilerHomeView can import them
// without a circular dependency on App.
// Declared where it is assigned, the same way src/lib/supabase.ts declares the
// `window.sb` bridge it installs. Optional because the assignment below is
// guarded on `typeof window !== 'undefined'`, so a non-browser host genuinely
// never receives it.
declare global {
  interface Window {
    invalidateCattleWeighInsCache?: typeof invalidateCattleWeighInsCache;
  }
}

if (typeof window !== 'undefined') {
  window.invalidateCattleWeighInsCache = invalidateCattleWeighInsCache;
}

// Phase 2 Round 3: bigger stateful views + UsersModal.

// Phase 2 Round 4: admin panels.

// Phase 2 Round 5: public webforms (no-auth hash-routed).

// Phase 2 Round 8: equipment placeholder.

// Pasture Map was the first route-level performance boundary and remains the
// largest deep-dependency cluster in the app (leaflet + leaflet-geoman + turf
// + togeojson, isolated to src/pasture/ and its own lib files — see
// PROJECT.md). Its auth-gated split established the pattern now generalized
// across secondary AppEntry and independently released-surface routes; the
// home surfaces remain eager so their first render does not wait on another
// route chunk.

// Public, no-login Monthly Newsletter archive. Mounted in a bypass block above
// the LoginScreen gate (see the PUBLIC NEWSLETTER block in App's render). Self-
// contained: talks only to the three anon newsletter RPCs.
// Admin Monthly Newsletter workspace (admin-only via UnauthorizedRedirect).

// Phase 2 Round 2 tail: LayerBatchesView (deferred from the original Round 2
// push). Now imports once the housing + broiler primitives live in lib/.

// Phase 2 Round 7: HomeDashboard — the last big inline view. Hook-based
// extraction; reads every data context, takes the operational helpers it
// VIEW_TO_PROGRAM as props (they still live in App).

// Phase 2 Round 6 tail: top nav bar + program sub-nav. Pulls view/menu
// state + auth + form-open booleans from their respective contexts; App()
// wraps this in a local Header closure below so every extracted view can
// still receive a zero-arg <Header/> prop (no ripple changes).

// Phase 2 Round 6 tail: broiler add/edit modal — the last inline block
// inside App's render body. Hook-based; receives the seven operational
// helpers (upd/closeForm/submit/del/openEdit/parseProcessorXlsx/persist) as
// props from App. `confirmDelete` is NOT among them: it comes from
// ConfirmContext at each consumer.

// ── RUNTIME BOOT STEPS ──
// The legacy babel-cache purge, the hash-bookmark compat rewrite, the manifest
// href swap, service-worker registration, and the lazy SheetJS bridge now live
// in src/app/bootstrap/runtimeInit.js. initRuntime() is synchronous and MUST
// run before the first render: the hash rewrite has to land before
// BrowserRouter reads pathname, or a legacy `#webforms` bookmark boots to home.
// applyManifestHref is re-exported through that module because App also calls
// it from a useEffect keyed on location.pathname.

// === BEGIN VERBATIM PORT (was index.html lines 212–19342) ===

// (Phase 2.0.0: sb client init + wcfSendEmail + wcfSelectAll definitions
// moved to src/lib/supabase.js, src/lib/email.js, src/lib/pagination.js
// respectively. They're re-imported above as module-scoped names so every
// downstream reference in the verbatim-ported body still resolves.)

// Phase 2.3 prep: cattle cache helpers moved to src/lib/cattleCache.js.

const {useState, useEffect} = React;

// ── CONSTANTS ──────────────────────────────────────────────────────────────
// BROODER_CLEANOUT + SCHOONER_CLEANOUT moved to lib/broiler.js (imported above).
// Hatchery lists + STATUSES + BROODERS + SCHOONERS moved to lib/broiler.js
// (imported above). CC_HATCHERIES / WR_HATCHERIES were legacy-only refs that
// no live code consumed — dropped.

// ── CATTLE CONSTANTS ──
// Cattle constants live in src/lib/cattle.ts and are consumed by the breeding
// helpers and views directly.

// Given a bull exposure start date, return the full cycle timeline.
// All dates ISO strings. Returns null if start not provided.
// Phase 2.3 prep: cattle breeding helpers moved to src/lib/cattleBreeding.js.

// B2.1: INITIAL_BREEDERS moved to src/app/data/pigSeedData.js.

// B2.1: INITIAL_FARROWING moved to src/app/data/pigSeedData.js.

// ── Layer housing count helpers (top-level, no React state) ────────────────
// Model X: current_count is a *verified anchor* set by physical counts (manual
// or via daily report layer_count). Mortality reports do NOT mutate it.
// Projected count is computed at display time from anchor minus mortalities.

// Update housing anchor when a daily report includes a hen count.
// Sets current_count to the new value AND current_count_date to the report date.
// Phase 2.1 prep: setHousingAnchorFromReport + computeProjectedCount + computeLayerFeedCost moved to src/lib/layerHousing.js (imported at top of file).

// ── DATE HELPERS ───────────────────────────────────────────────────────────
// Phase 2.3 prep: date utils moved to src/lib/dateUtils.js.

// ── BUSINESS LOGIC ─────────────────────────────────────────────────────────

// calcTargetHatch + suggestHatchDates + overlaps() moved to lib/broiler.js.
// detectConflicts moved to lib/conflicts.js (imported above).

// ── EMPTY FORM ─────────────────────────────────────────────────────────────
// B2.1: EMPTY_FORM moved to src/app/data/broilerFormDefaults.js.

// ══════════════════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════════════════

// Shown when the user lands via a password reset / invite link. Supabase has
// already exchanged the recovery token for a session, but we hold them here
// to set their password before they reach the home screen. After update, we
// clear the recovery flag and they continue into the app already signed in.
// Phase 2.1.5: SetPasswordScreen moved to src/auth/SetPasswordScreen.jsx.

// Phase 2.1.5: LoginScreen moved to src/auth/LoginScreen.jsx.;

// Phase 2 Round 5 fix: S styles object moved to src/lib/styles.js.

// ── PERMISSION HELPERS ──────────────────────────────────────
function canDeleteAnything(role: string | undefined) {
  return role === 'admin';
}

// ── USERS MODAL ── (standalone component — must NOT be nested inside App)
// Phase 2 Round 3: UsersModal moved out of this file. It now lives at
// src/shared/UsersModal.tsx (moved from src/auth/ so ten domains stop importing
// through the auth surface).

// ── ADD FEED WEBFORM ──────────────────────────────────────────────────────────
// Public webform for quick feed logging. Inserts a new row into the appropriate
// *_dailys table with source='add_feed_webform'. No merge, no collision check.
// Admin-configurable: fields can be toggled on/off, marked required, relabeled.
// Supports Add Group (multiple batch+feed entries per submit).
// Phase 2 Round 5: AddFeedWebform moved to C:\Users\Ronni\WCF-planner\src\webforms\AddFeedWebform.jsx.

// writeBroilerBatchAvg moved to lib/broiler.js. Its two callers
// (WeighInsWebform + LivestockWeighInsView) now import it directly.

// ── WEIGH-INS WEBFORM ──────────────────────────────────────────────────────
// Public webform for weigh-in sessions. Three species flows:
//   - Cattle: session-based with autosave + diminishing tag dropdown + new-tag flag
//   - Pig: per-row weights for an active feeder batch (no tag); send-to-trip later
//   - Broiler: 4-week / 6-week weighings, ~15 birds, average auto-fills batch
// All entries persist to weigh_in_sessions + weigh_ins immediately. Phone drop
// recoverable: re-open the form, pick "Resume" on a draft session.
// Phase 2 Round 5: WeighInsWebform moved to C:\Users\Ronni\WCF-planner\src\webforms\WeighInsWebform.tsx.

// Phase 2 Round 5: WebformHub moved to C:\Users\Ronni\WCF-planner\src\webforms\WebformHub.tsx.

// Phase 2 Round 4: FeedCostsPanel moved to C:\Users\Ronni\WCF-planner\src\admin\FeedCostsPanel.jsx.

// ── FEED COST BY MONTH PANEL ────────────────────────────────────────────────
// Admin \u2192 Cost by Month. Aggregates feed spend across programs by month.
// Pig/Broiler/Layer multiply feed_lbs by the per-lb rates in FeedCostsPanel.
// Cattle multiplies each daily's feed-line qty by the matching feed-input
// cost_per_unit from the Cattle & Sheep Inputs panel.
// Current costs are used for all rows (no per-month historical cost ledger),
// so retroactive price changes affect historical totals.

// ── LIVESTOCK FEED INPUTS PANEL ─────────────────────────────────────────────
// Admin → Feed sub-section. Full feed master list with nutrition, costs, herd
// scoping. Reusable for cattle today, sheep later. Autosave on close.
// Test PDF upload + version history is built out separately (Phase 1 step 5).
// Phase 2 Round 4: LivestockFeedInputsPanel moved to C:\Users\Ronni\WCF-planner\src\admin\LivestockFeedInputsPanel.jsx.

// ── NUTRITION TARGETS PANEL ─────────────────────────────────────────────────
// Admin → Feed sub-section. Per-herd DM/CP/NFC target percentages used by the
// dashboard rolling-window comparison and the recommendation engine.
// Phase 2 Round 4: NutritionTargetsPanel moved to C:\Users\Ronni\WCF-planner\src\admin\NutritionTargetsPanel.jsx.

// Phase 2.1.2: DeleteModal moved to src/shared/DeleteModal.jsx.

const AUTH_BOOT_TIMEOUT_MS = 15000;
const AUTH_VISIBILITY_REFRESH_THROTTLE_MS = 60000;

// ── TYPES FOR THE LEGACY ESTATE THIS COMPONENT STILL OWNS ──────────────────
//
// `LegacyBlob` is the ONE `any` token in this file. Every use of it is spelled
// with this name rather than written inline, so the whole loose surface is
// greppable and countable in one command.
//
// What it covers, and why each is genuinely unschema'd HERE:
//
//   * app_store jsonb blobs (`ppp-v4`, `ppp-feeders-v1`, `ppp-webforms-v1`, …).
//     They live in one `data jsonb` column, this repository has no generated
//     database types, and App is the module that WRITES them. The loader below
//     migrates them field by field.
//   * the broiler add/edit form and broiler batch records. BatchesContext types
//     these `unknown` on purpose (see its header: it never reads a field off
//     them, so inventing a row interface there would be a claim it cannot
//     check). App is the writer, and the code already assumes the shape —
//     `parseFloat()` is applied to values that may already be numbers, and a
//     form field is read back as whatever the input last stored.
//   * two narrow spots where the value really is untyped at runtime: the
//     `profiles` row and a caught error's `.message`. (The third — the
//     former two-element layer Promise.all result was retired with the global
//     boot loader; route-owned layer resources now name the fields they read.)
//
// Naming a shape for any of them would be inventing a contract. Nothing leaves
// App as `LegacyBlob`: every prop boundary (BroilerEntry, PigEntry, LayerEntry,
// WebformsEntry, AppEntry, BatchForm) re-narrows to `unknown` / `unknown[]`, so
// the looseness cannot propagate out of this module.
type LegacyBlob = any;

// The three members of BatchesContext App treats as the broiler form estate.
// Restated ONCE, here, rather than asserted at each of the ~15 reads below.
type BatchesContextForApp = Omit<ReturnType<typeof useBatches>, 'batches' | 'form' | 'originalForm'> & {
  batches: LegacyBlob[];
  form: LegacyBlob;
  originalForm: LegacyBlob;
};

// Same treatment for the three other contexts whose rows App reads fields off.
type LayerContextForApp = Omit<ReturnType<typeof useLayer>, 'layerGroups' | 'layerBatches'> & {
  layerGroups: LegacyBlob[];
  layerBatches: LegacyBlob[];
};

type PigContextForApp = Omit<ReturnType<typeof usePig>, 'feederGroups'> & {
  feederGroups: LegacyBlob[];
};

type DailysRecentContextForApp = Omit<ReturnType<typeof useDailysRecent>, 'broilerDailys'> & {
  broilerDailys: LegacyBlob[];
};

// The Supabase auth user, derived from the client this app already creates
// rather than through a second import of the Supabase library —
// tests/static/supabase_client_owner_static.test.js counts that specifier as
// TEXT, so even an erased `import type` of it would break the single-owner
// boundary. Identical derivation to src/contexts/AuthContext.tsx.
type SupabaseAuthUser = NonNullable<Awaited<ReturnType<typeof sb.auth.getUser>>['data']['user']>;

// The maintenance coordinator and the narrow client shape it declares, both
// derived from the factory rather than imported: App gains no new module edge
// for a type alone, and webform_mirror_stale_closure_static pins the exact
// single-name import line above.
type AuthenticatedSurface = {
  view: string;
  location: ReturnType<typeof useAppLocation>;
  showForm: boolean;
  editId: unknown;
};

// What triggerMaintenance's caller may override for one pass. Every member is
// the value that boot actually resolved, which is why they are all optional
// and all opaque — the coordinator's own MaintenanceSnapshot is equally open.

function App() {
  // ── AUTH & LOADING STATE ──
  // Phase 2.0.1: these hooks live in AuthContext. See src/contexts/AuthContext.jsx
  // for the pwRecovery URL-hash initializer and other state defaults. Effects
  // (auth listener, visibility refresh, access-gate redirect), helpers
  // (loadUser), and derived values remain here.
  const {
    authState,
    setAuthState,
    pwRecovery,
    setPwRecovery,
    sessionEpoch,
    setSessionEpoch,
    sessionRunId,
    setSessionRunId,
    profileStatus,
    setProfileStatus,
    registerSessionLifecycle,
    setSaveStatus,
  } = useAuth();

  // Phase 2.0.2: broiler batch + edit-form hooks live in BatchesContext.
  const {
    batches,
    setBatches,
    showForm,
    setShowForm,
    editId,
    setEditId,
    form,
    setForm,
    originalForm,
    setOriginalForm,
    conflicts,
    setConflicts,
    setOverride,
    setShowLegacy,
    setParsedProcessor,
    setFormNotice,
  } = useBatches() as BatchesContextForApp;

  // Phase 2.0.3: pig-scoped hooks live in PigContext.
  const {feederGroups} = usePig() as PigContextForApp;

  // Phase 2.0.4: layer-scoped hooks live in LayerContext.
  const {layerGroups, setLayerGroups, layerBatches} = useLayer() as LayerContextForApp;

  // Phase 2.0.5: recent-window dailys arrays live in DailysRecentContext.
  const {broilerDailys, setBroilerDailys, setPigDailys} = useDailysRecent() as DailysRecentContextForApp;

  // Phase 2.0.6 — small bundled contexts.
  const {feedCosts} = useFeedCosts();
  const {view, viewRequest, setView} = useUI();
  // Design Constitution Q41: the tab title is 'Page · Program · WCF', from the
  // navigation's own labels. The public newsletter sets its own title.
  React.useEffect(() => {
    if (view === 'newsletter') return;
    document.title = titleForView(view);
  }, [view]);
  // B2.2c: the confirmation overlays and their openers live in ConfirmContext.
  const {confirmDelete} = useConfirm();

  // Permission helpers — role-based access
  // farm_team: edit+delete own dailys only
  // management: edit anything, delete dailys only
  // admin: full access
  // The assertion widens away the `false` member so the existing optional chain
  // type-checks; `false?.role` and `(false as AuthIdentity | null)?.role` both
  // evaluate to undefined, so the runtime read is unchanged.
  const role = (authState as AuthIdentity | null)?.role;

  // Phase 3.2: URL ↔ view adapter. The existing `view` state machine stays
  // the primary API — setView('X') still works from every component. These
  // effects mirror `view` into the URL and read URL → view on mount +
  // back/forward button. No changes required at any setView call site.
  //
  // Infinite-loop guard: syncingFromUrl flag distinguishes URL-driven view
  // changes (skip the view→URL effect) from code-driven view changes
  // (do push a URL entry so the back button works).
  // The current BrowserRouter remains the sole history owner in B2.  App reads
  // and writes through the navigation facade so this same compatibility state
  // machine can later sit under the one TanStack RouterProvider without a
  // second router or a duplicate URL synchronizer.
  const location = useAppLocation();
  const navigate = useAppNavigate();
  const syncingFromUrl = React.useRef(false);
  // N10: a path no route owns shows 'We can't find that page' in place,
  // under the header, instead of snapping to Home. Holds that pathname until
  // the URL moves on.
  const [unknownPath, setUnknownPath] = React.useState<string | null>(null);
  // True until the URL->view effect has run once. On that first run the
  // view->URL effect runs right after it and must not push the unknown path
  // away; later in-app navigations to an unknown path leave the view (and so
  // that effect) untouched.
  const firstUrlSync = React.useRef(true);
  useEffect(() => {
    // 2026-05-06 alias resolution — legacy public paths redirect to canonical.
    // /webforms* → /dailys*, /fueling* → /equipment*, /equipment/fleet →
    // /fleet, /equipment/fuel-log → /fleet/fuel-log. Use the active
    // navigation driver's replace operation so the address bar and its one
    // history owner stay in sync (raw history.replaceState would skip that
    // owner and break popstate handling).
    const pathAlias = resolvePathAlias(location.pathname);
    if (pathAlias) {
      syncingFromUrl.current = true;
      navigate(pathAlias + location.search + location.hash, {replace: true});
      return;
    }

    // WebformHub owns its own sub-routing under /dailys/<form>. Treat any
    // such path as view='webformhub' so the browser back button can traverse
    // selector ↔ sub-form boundaries without the app snapping to home.
    //
    // EXCEPT for paths that have an explicit PATH_TO_VIEW entry — those
    // are dedicated top-level views that happen to live under /dailys/.
    // Currently: /dailys/tasks → 'tasksWebform' (C3). The exact-match
    // PATH_TO_VIEW lookup beats the generic subpath fallback so the
    // dedicated component mounts instead of WebformHub trying to render
    // it as an internal sub-form.
    const exactPathView = PATH_TO_VIEW[location.pathname];
    const isWebformSubpath = !exactPathView && location.pathname.startsWith('/dailys/');
    const isEquipmentSubpath =
      !exactPathView && (location.pathname.startsWith('/fleet/') || location.pathname === '/fleet');
    const isFuelingSubpath =
      !exactPathView && (location.pathname.startsWith('/equipment/') || location.pathname === '/equipment');
    const isCattleHerdsSubpath = !exactPathView && location.pathname.startsWith('/cattle/herds/');
    const isCattleBatchesSubpath = !exactPathView && location.pathname.startsWith('/cattle/batches/');
    const isSheepFlocksSubpath = !exactPathView && location.pathname.startsWith('/sheep/flocks/');
    const isSheepBatchesSubpath = !exactPathView && location.pathname.startsWith('/sheep/batches/');
    const isBroilerDailysSubpath = !exactPathView && location.pathname.startsWith('/broiler/dailys/');
    const isPigDailysSubpath = !exactPathView && location.pathname.startsWith('/pig/dailys/');
    const isPigFarrowingSubpath = !exactPathView && location.pathname.startsWith('/pig/farrowing/');
    const isPigSowsSubpath = !exactPathView && location.pathname.startsWith('/pig/sows/');
    const isCattleDailysSubpath = !exactPathView && location.pathname.startsWith('/cattle/dailys/');
    const isSheepDailysSubpath = !exactPathView && location.pathname.startsWith('/sheep/dailys/');
    const isLayerDailysSubpath = !exactPathView && location.pathname.startsWith('/layer/dailys/');
    const isEggDailysSubpath = !exactPathView && location.pathname.startsWith('/layer/eggs/');
    const isLayerBatchesSubpath = !exactPathView && location.pathname.startsWith('/layer/batches/');
    const isLayerHousingsSubpath =
      !exactPathView && (location.pathname === '/layer/housings' || location.pathname.startsWith('/layer/housings/'));
    const isBroilerBatchesSubpath = !exactPathView && location.pathname.startsWith('/broiler/batches/');
    const isPigBatchesSubpath = !exactPathView && location.pathname.startsWith('/pig/batches/');
    const isTasksSubpath = !exactPathView && location.pathname.startsWith('/tasks/');
    // This view has no VIEW_TO_PATH entry, so retain the bare splat base as
    // well as descendants until the explicit disposition gate can render its
    // terminal recovery. Letting the generic unknown-path fallback send the
    // base home would hide the malformed route instead of fencing it.
    const isWeighInSessionRoute =
      !exactPathView &&
      (location.pathname === '/weigh-in-sessions' || location.pathname.startsWith('/weigh-in-sessions/'));
    // Cattle Breeding itself is an exact release, but every descendant must
    // stay paired with its view long enough for the exact release boundary to
    // terminal-recover it. The generic unknown-path fallback would otherwise
    // silently rewrite an invalid raw suffix to Home.
    const isCattleBreedingSubpath = !exactPathView && location.pathname.startsWith('/cattle/breeding/');
    // Production itself is an exact release. Keep malformed descendants paired
    // with that view until the release boundary can terminal-recover them.
    const isProductionSubpath = !exactPathView && location.pathname.startsWith('/production/');
    // Public newsletter owns its own pathname routing under /newsletter/* (latest
    // + issue slug + preview). /newsletter itself resolves via exactPathView.
    const isNewsletterSubpath = !exactPathView && location.pathname.startsWith('/newsletter/');
    // The cut-sheet editor is the `weighinsessions` arrangement, not the
    // `/processing` one: a splat row with NO VIEW_TO_PATH entry, so nothing but
    // this branch can pair the URL with its view and an unpaired address goes to
    // Home. Unlike every sibling above it, the test is the route contract's own
    // predicate rather than a `startsWith` written here — `/processing/cut-sheet/`
    // names a Processing record that goes straight into an RPC argument, and
    // `src/app/boot/cutSheetRecordPaths.ts` is where the one accepted shape is
    // decided. A bare prefix test here would admit the shapes that fence refuses
    // and then disagree with the released boundary about the same URL.
    //
    // `/processing` itself is UNAFFECTED: it is an exact VIEW_TO_PATH row, so it
    // resolves through `exactPathView`, and this prefix is strictly longer than
    // it, so neither can reach the other. Malformed descendants
    // (`/processing/cut-sheet`, `/processing/cut-sheet/`, an id carrying an
    // encoded separator) deliberately fail this test and take the unknown-path
    // fallback to Home — they name no record, so there is nothing to fence them
    // for and no editor state to lose.
    const isCutSheetEditorRoute = !exactPathView && isCanonicalCutSheetEditorPathname(location.pathname);
    // A40: the Admin equipment record page is the same arrangement as the
    // cut-sheet editor (splat row, no VIEW_TO_PATH entry), so this branch is
    // the only thing that pairs its URL with its view, and the test is the
    // route contract's own predicate (src/app/boot/adminEquipmentRecordPath.ts).
    // `/admin` stays exact; malformed descendants (`/admin/equipment`,
    // `/admin/equipment/`, nested segments) fail it and show Not Found.
    const isAdminEquipmentRecordRoute = !exactPathView && isCanonicalAdminEquipmentRecordPathname(location.pathname);
    const viewFromUrl = isWebformSubpath
      ? 'webformhub'
      : isEquipmentSubpath
        ? 'equipmentHome'
        : isFuelingSubpath
          ? 'fuelingHub'
          : isCattleHerdsSubpath
            ? 'cattleherds'
            : isCattleBatchesSubpath
              ? 'cattlebatches'
              : isSheepFlocksSubpath
                ? 'sheepflocks'
                : isSheepBatchesSubpath
                  ? 'sheepbatches'
                  : isBroilerDailysSubpath
                    ? 'broilerdailys'
                    : isPigDailysSubpath
                      ? 'pigdailys'
                      : isPigFarrowingSubpath
                        ? 'farrowing'
                        : isPigSowsSubpath
                          ? 'sows'
                          : isCattleDailysSubpath
                            ? 'cattledailys'
                            : isSheepDailysSubpath
                              ? 'sheepdailys'
                              : isLayerDailysSubpath
                                ? 'layerdailys'
                                : isEggDailysSubpath
                                  ? 'eggdailys'
                                  : isLayerBatchesSubpath
                                    ? 'layerbatches'
                                    : isLayerHousingsSubpath
                                      ? 'layerbatches'
                                      : isBroilerBatchesSubpath
                                        ? 'list'
                                        : isPigBatchesSubpath
                                          ? 'pigbatches'
                                          : isTasksSubpath
                                            ? 'tasks'
                                            : isWeighInSessionRoute
                                              ? 'weighinsessions'
                                              : isCattleBreedingSubpath
                                                ? 'cattlebreeding'
                                                : isProductionSubpath
                                                  ? 'production'
                                                  : isNewsletterSubpath
                                                    ? 'newsletter'
                                                    : isCutSheetEditorRoute
                                                      ? 'processingCutSheet'
                                                      : isAdminEquipmentRecordRoute
                                                        ? 'equipmentAdminRecord'
                                                        : exactPathView;
    if (viewFromUrl && viewFromUrl !== view) {
      syncingFromUrl.current = true;
      setView(viewFromUrl);
    } else if (!viewFromUrl) {
      // Unknown path. Aliases were redirected above, and the hash-compat shim
      // (Phase 3.3) handles legacy /#weighins etc. before this effect sees
      // the pathname.
      //
      // An auth link (password recovery, invite, magic link, or its error)
      // keeps the old snap to Home with location.hash preserved: a URL like
      // /garbage#access_token=...&type=recovery would otherwise lose its
      // hash, breaking supabase's password-recovery flow. SetPasswordScreen
      // reads window.location.hash directly, so surviving to / with the
      // hash intact lets the recovery listener fire normally.
      if (AUTH_LINK_HASH.test(location.hash)) {
        syncingFromUrl.current = true;
        setView('home');
        navigate('/' + location.hash, {replace: true});
      } else {
        // Every other unknown path stays put and shows the Not Found card
        // (N10). The view is untouched. On the very first sync the view->URL
        // effect still runs once (mount), so it is told to skip that run.
        if (firstUrlSync.current) syncingFromUrl.current = true;
        setUnknownPath(location.pathname);
      }
    }
    if (viewFromUrl) setUnknownPath(null);
    firstUrlSync.current = false;
  }, [location.pathname]);

  // Keep link[rel="manifest"] in sync with the active hub on SPA navigation.
  // The module-scope shim above sets the link before first paint; this
  // effect handles transitions between hubs without a full reload (e.g.
  // navigating from /dailys to /equipment via the Header buttons).
  useEffect(() => {
    applyManifestHref(location.pathname);
  }, [location.pathname]);

  // Clear the full-screen broiler form flag when the URL leaves broiler space.
  // The render guard below is what makes the form unable to PAINT off-broiler;
  // this is the hygiene half. A stale flag still forces the Header's program
  // selection to 'broiler' on every surface and would silently re-open the form
  // on returning to a broiler URL, including across a sign-out and sign-in,
  // because BatchesProvider mounts once above the router and never remounts.
  // /broiler/batches/<id> is excluded: BroilerBatchPage owns the flag there.
  useEffect(() => {
    const inBroiler = location.pathname === '/broiler' || location.pathname.startsWith('/broiler/');
    const isBatchRecord = location.pathname.startsWith('/broiler/batches/');
    if (!inBroiler && !isBatchRecord) setShowForm(false);
  }, [location.pathname, setShowForm]);

  useEffect(() => {
    if (syncingFromUrl.current) {
      syncingFromUrl.current = false;
      return;
    }
    // Don't clobber /dailys/<form> sub-paths — WebformHub owns them.
    if (view === 'webformhub' && location.pathname.startsWith('/dailys/')) return;
    // Don't clobber /fleet/<slug> sub-paths — EquipmentHome owns them.
    if (view === 'equipmentHome' && location.pathname.startsWith('/fleet/')) return;
    // Don't clobber /newsletter/<slug> (or /latest, ?preview) — the public
    // newsletter app owns its own pathname routing under /newsletter*.
    if (view === 'newsletter' && location.pathname.startsWith('/newsletter')) return;
    // Don't clobber /equipment/<slug> sub-paths — FuelingHub owns them.
    if (view === 'fuelingHub' && location.pathname.startsWith('/equipment/')) return;
    // Don't clobber /cattle/herds/<id> sub-paths — CattleHerdsView owns them.
    if (view === 'cattleherds' && location.pathname.startsWith('/cattle/herds/')) return;
    if (view === 'cattlebatches' && location.pathname.startsWith('/cattle/batches/')) return;
    // Don't clobber /sheep/flocks/<id> sub-paths — SheepFlocksView owns them.
    if (view === 'sheepflocks' && location.pathname.startsWith('/sheep/flocks/')) return;
    // Don't clobber /sheep/batches/<id> sub-paths — SheepBatchesRouter owns them.
    if (view === 'sheepbatches' && location.pathname.startsWith('/sheep/batches/')) return;
    // Don't clobber daily record sub-paths — each DailysView router owns them.
    if (view === 'broilerdailys' && location.pathname.startsWith('/broiler/dailys/')) return;
    if (view === 'pigdailys' && location.pathname.startsWith('/pig/dailys/')) return;
    if (view === 'farrowing' && location.pathname.startsWith('/pig/farrowing/')) return;
    if (view === 'sows' && location.pathname.startsWith('/pig/sows/')) return;
    if (view === 'cattledailys' && location.pathname.startsWith('/cattle/dailys/')) return;
    if (view === 'sheepdailys' && location.pathname.startsWith('/sheep/dailys/')) return;
    if (view === 'layerdailys' && location.pathname.startsWith('/layer/dailys/')) return;
    if (view === 'eggdailys' && location.pathname.startsWith('/layer/eggs/')) return;
    // Don't clobber /layer/batches/<id> or /layer/housings/<id> sub-paths — LayerBatchesRouter owns them.
    if (view === 'layerbatches' && location.pathname.startsWith('/layer/batches/')) return;
    if (
      view === 'layerbatches' &&
      (location.pathname === '/layer/housings' || location.pathname.startsWith('/layer/housings/'))
    ) {
      return;
    }
    // Don't clobber /broiler/batches/<id> sub-paths — BroilerListRouter owns them.
    if (view === 'list' && location.pathname.startsWith('/broiler/batches/')) return;
    // Don't clobber /pig/batches/<id> sub-paths — PigBatchesView owns them.
    if (view === 'pigbatches' && location.pathname.startsWith('/pig/batches/')) return;
    if (view === 'tasks' && location.pathname.startsWith('/tasks/')) return;
    if (
      view === 'weighinsessions' &&
      (location.pathname === '/weigh-in-sessions' || location.pathname.startsWith('/weigh-in-sessions/'))
    ) {
      return;
    }
    if (view === 'cattlebreeding' && location.pathname.startsWith('/cattle/breeding/')) return;
    if (view === 'production' && location.pathname.startsWith('/production/')) return;
    const pathFromView = VIEW_TO_PATH[view as keyof typeof VIEW_TO_PATH];
    if (pathFromView && pathFromView !== location.pathname) {
      navigate(pathFromView);
    }
  }, [view]);

  // N10: on the Not Found page the URL belongs to no view, so choosing the
  // view that is already current (Home from /no/such/page, Tasks after
  // wandering off /tasks) must still leave. UIContext counts those same-view
  // requests; only the Not Found page acts on them, so every valid page,
  // record URL and auth-link snap keeps its existing same-view no-op.
  const handledViewRequest = React.useRef(viewRequest);
  useEffect(() => {
    if (handledViewRequest.current === viewRequest) return;
    handledViewRequest.current = viewRequest;
    if (unknownPath === null || unknownPath !== location.pathname) return;
    const requestedPath = VIEW_TO_PATH[view as keyof typeof VIEW_TO_PATH];
    if (requestedPath) navigate(requestedPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewRequest]);

  // Seeded with `null` exactly as before. The type says "a timer handle" rather
  // than "a timer handle or null" so `clearTimeout(ref.current)` still
  // type-checks against lib.dom's `clearTimeout(id: number | undefined)` — the
  // same seed src/admin/LivestockFeedInputsPanel.tsx uses for its autosave ref.
  const autoSaveTimer = React.useRef<ReturnType<typeof setTimeout>>(null as unknown as ReturnType<typeof setTimeout>);
  const pigAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout>>(
    null as unknown as ReturnType<typeof setTimeout>,
  );
  const subAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout>>(
    null as unknown as ReturnType<typeof setTimeout>,
  );
  const tripAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout>>(
    null as unknown as ReturnType<typeof setTimeout>,
  );
  // `breedAutoSaveTimer` USED TO LIVE HERE. The 500 ms Gantt-form debounce is
  // /pig/breeding's own and nothing else ever touched the handle, so it moved
  // into src/pig/BreedingView.tsx with the write it debounces. It is at MODULE
  // scope there, deliberately: hoisting it into App gave the handle an
  // app-lifetime that a `useRef` inside the view would not reproduce, and a
  // pending save orphaned by a remount is the one behaviour that change would
  // have altered.
  // The session coordinator owns profile-load lease lifetime and run revisions.
  // It intentionally survives the all-data loader's retirement: released claims
  // still need a current subject/epoch fence before they can render.
  const legacyBootCoordinatorRef = React.useRef<LegacyBootCoordinator | null>(null);
  if (legacyBootCoordinatorRef.current === null) {
    legacyBootCoordinatorRef.current = createLegacyBootCoordinator();
  }
  const activeLegacyLeaseRef = React.useRef<LegacyBootLease | null>(null);
  // A released surface is tied to the exact session/run and current routed
  // candidate. Revalidate it immediately before rendering; no fallback path is
  // allowed to authorize a global data load after this boundary rejects it.
  const activeReleasedSurfaceClaimRef = React.useRef<ReleasedSurfaceClaim | null>(null);
  // The auth subscription is intentionally mounted once, so its callback must
  // not keep the route/view closure from that first render. Write only after a
  // render commits; the resolver then sees one coherent current surface when a
  // fresh session lease reaches profile resolution.
  const authenticatedSurfaceRef = React.useRef({view, location, showForm, editId});
  useEffect(() => {
    authenticatedSurfaceRef.current = {view, location, showForm, editId};
  }, [view, location, showForm, editId]);
  // A negative getSession/getUser result is also async data. It may have been
  // requested before a SIGNED_IN event accepted a newer subject, so it needs a
  // small intake generation in addition to the boot lease's positive path.
  const sessionIntakeGenerationRef = React.useRef(0);
  const manualSessionEndRef = React.useRef(false);
  // The three state types below are transcribed from the prop types the domain
  // entries already declare (src/pig/PigEntry.tsx, src/broiler/BroilerEntry.tsx)
  // rather than invented here, so App and its consumers cannot drift.
  const [feedOrders, setFeedOrders] = useState<Record<string, Record<string, unknown>>>({
    pig: {},
    starter: {},
    grower: {},
    layerfeed: {},
  });
  const [pigFeedInventory, setPigFeedInventory] = useState<Record<string, unknown> | null>(null); // {count, date} or null
  const [poultryFeedInventory, setPoultryFeedInventory] = useState<Record<string, unknown> | null>(null); // {starter:{count,date}, grower:{count,date}, layer:{count,date}}
  // wfForm/wfSubmitting/wfDone/wfErr/wfGroupName moved into
  // src/webforms/PigDailysWebform.jsx as internal state — App no longer
  // needs them; they were only ever read by the extracted webform.
  const [collapsedBatches, setCollapsedBatches] = useState<Set<string>>(new Set());
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(() => {
    // Auto-collapse past months on init
    const s = new Set<string>();
    // We'll populate this in the feed view
    return s;
  });

  // Refreshes App-owned dailys projections for the remaining global callers
  // and the Recently Deleted recovery action. Cattle, Egg, Layer, and Sheep
  // each own their action-time full-row projection/recovery reads locally;
  // none captures this function.
  async function refreshDailys(kind: string) {
    const want = (k: string) => kind === 'all' || kind === k;
    if (want('broiler')) {
      // Reuse the one full-history loader already used at boot. It throws on
      // every page-read failure rather than publishing a silent partial; this
      // action-time caller deliberately keeps the last complete context value
      // on failure because the Broiler mutation paths invoke it fire-and-forget.
      try {
        const all = await loadAllPoultryDailys(sb);
        setBroilerDailys(all);
      } catch (e) {
        console.warn('poultry_dailys refresh error:', e && (e as LegacyBlob).message ? (e as LegacyBlob).message : e);
      }
    }
    if (want('pig')) {
      try {
        const all = await loadAllPigDailys(sb);
        setPigDailys(all);
      } catch (e) {
        console.warn('pig_dailys refresh error:', e && (e as LegacyBlob).message ? (e as LegacyBlob).message : e);
      }
    }
  }

  // Guard: unknown views fall back to home (must be unconditional)
  const VALID_VIEWS = [
    'home',
    'animalHistory',
    'production',
    'broilerHome',
    'pigsHome',
    'layersHome',
    'timeline',
    'list',
    'feed',
    'pigfeed',
    'pigs',
    'breeding',
    'pigbatches',
    'farrowing',
    'sows',
    'webforms',
    'tasks',
    'activity',
    'projects',
    'clientErrors',
    'webformhub',
    'tasksWebform',
    'webform',
    'broilerdailys',
    'pigdailys',
    'layers',
    'layerbatches',
    'layerdailys',
    'eggdailys',
    'addfeed',
    'weighins',
    'cattleHome',
    'cattleherds',
    'cattledailys',
    'cattleweighins',
    'cattlebreeding',
    'cattleforecast',
    'cattlebatches',
    'cattlelog',
    'broilerweighins',
    'pigweighins',
    'sheepHome',
    'sheepflocks',
    'sheepbatches',
    'sheepdailys',
    'sheepweighins',
    'equipmentHome',
    'fuelingHub',
    'fuelSupply',
    'weighinsessions',
    'mySubmissions',
    'pastureMap',
    'weather',
    'processing',
    'processingCutSheet',
    'newsletter',
    'newsletterAdmin',
    'equipmentAdminRecord',
  ];
  useEffect(() => {
    if (view && !VALID_VIEWS.includes(view)) setView('home');
  }, [view]);

  // ── SURFACE ACCESS — one decision, two consumers ──
  // B3.3 Prerequisite C. This used to be four independent answers to the same
  // question: a per-program bounce, a Light containment bounce, a processing
  // role bounce, and a fail-closed Light render guard further down, each
  // re-deriving isLight/role/the cattle-log carve-out for itself. A
  // route-local data loader cannot be added safely on top of four answers that
  // are only incidentally consistent, so they are now one pure function in
  // src/lib/access.js.
  //
  // The rules are unchanged and that is proved, not asserted: src/lib/
  // access.test.ts transcribes the previous inline logic verbatim and runs
  // both over the whole role x profile x view matrix, requiring agreement on
  // every case, and requiring the corpus to reach every branch.
  //
  // Both carve-outs survive intact. A Light user's allowlisted daily views map
  // to a program but canAccessProgram is false for Light, so the allowlist
  // exempts them from the program bounce. Cattle Log is role-gated rather than
  // program-gated — the server RPCs check role only — so a farm_team or
  // management user whose program_access excludes cattle still reaches
  // /cattle/log, while equipment_tech and inactive do not.
  //
  // surfaceAccess.mayLoadProgramData is the NEW half and the reason this
  // exists: it is deliberately stricter than navigation. canAccessProgram()
  // returns true when the profile is absent, which is a reasonable tolerance
  // for navigation but fail-OPEN for data. A resource boundary asks
  // mayLoadProgramData, so a timed-out profile issues no program requests.
  // Two assertions, no runtime change. The inner one widens `authState` back to
  // the full union so the (already redundant) `!== false` comparison is not
  // reported as a non-overlapping one; the outer one records that every reader
  // of `isLight` sits BELOW the `authState === null` / `=== false` gates, where
  // the expression is a real boolean.
  const isLight = (authState && (authState as AuthState) !== false && authState.role === 'light') as boolean;
  // AuthContext keeps `profile` opaque (`unknown`); access.ts declares the one
  // field it reads (`program_access`). The resolver applies the identical
  // assertion after its own runtime narrowing — see
  // src/app/boot/authenticatedSurfaceResolver.ts.
  const surfaceAccess = resolveSurfaceAccess({
    authState: authState as AccessAuthState,
    view,
    viewToProgram: VIEW_TO_PROGRAM,
    isProcessingRole,
  });
  // N10 'Go Back': the nearest parent list this user may open, else Home. The
  // same access resolver decides; navigation stays with the facade.
  const goToParentRoute = () =>
    navigate(
      parentPathFor(
        location.pathname,
        (candidate) =>
          resolveSurfaceAccess({
            authState: authState as AccessAuthState,
            view: candidate,
            viewToProgram: VIEW_TO_PROGRAM,
            isProcessingRole,
          }).navigation === SURFACE_NAVIGATION.ALLOW,
      ),
      {replace: true},
    );
  useEffect(() => {
    // The processing branch stays gated on a RESOLVED role inside
    // resolveSurfaceAccess — otherwise the pre-auth window (role still
    // undefined) would strand a real operator on /processing home.
    //
    // Design Constitution N10 (PR9): a route this role may not open stays at its
    // URL and shows 'No Access — ask an admin' under the header (the terminal
    // ACCESS_DENIED disposition below); the decision and the loader gate are
    // unchanged, so nothing mounts or loads for it. Only Light containment still
    // snaps home, where the Light portal renders.
    if (surfaceAccess.navigation === SURFACE_NAVIGATION.LIGHT_PORTAL) setView('home');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, authState]);

  useEffect(() => {
    // Safety timeout — if auth hasn't resolved, make one last stored-session
    // pass before showing Login. Slow/offline starts should keep a refreshable
    // local session in-app whenever possible.
    let cancelled = false;
    let bootResolved = false;
    const authTimeout = setTimeout(() => {
      if (bootResolved || cancelled) return;
      const intakeGeneration = sessionIntakeGenerationRef.current;
      sb.auth
        .getSession()
        .then(async ({data: {session}}) => {
          if (cancelled || bootResolved) return;
          if (session?.user) {
            bootResolved = true;
            await acceptSession(session.user, intakeGeneration);
            return;
          }
          resolveUnauthenticatedIntake(intakeGeneration);
        })
        .catch(() => {
          if (!cancelled && !bootResolved) resolveUnauthenticatedIntake(intakeGeneration);
        });
    }, AUTH_BOOT_TIMEOUT_MS);

    async function verifyStoredSession(intakeGeneration: number) {
      try {
        const {data, error} = await sb.auth.getUser();
        if (cancelled) return;
        if (!error && data?.user) {
          await acceptSession(data.user, intakeGeneration);
          return;
        }
        const refreshed = await sb.auth.refreshSession();
        if (cancelled) return;
        if (refreshed?.data?.session?.user) {
          await acceptSession(refreshed.data.session.user, intakeGeneration);
          return;
        }
        if (!refreshed?.error) resolveUnauthenticatedIntake(intakeGeneration);
      } catch (e) {
        // Network/Auth service hiccups should not erase a stored refreshable
        // session. Keep the least recent loaded auth state and retry on the
        // next visibility/auth event.
        console.warn('Session verification failed; keeping stored session for retry.', e);
      }
    }

    async function bootAuth() {
      const intakeGeneration = sessionIntakeGenerationRef.current;
      try {
        const {
          data: {session},
        } = await sb.auth.getSession();
        if (cancelled) return;
        if (session?.user) {
          bootResolved = true;
          clearTimeout(authTimeout);
          await acceptSession(session.user, intakeGeneration);
          verifyStoredSession(sessionIntakeGenerationRef.current);
          return;
        }

        const {
          data: {user},
          error,
        } = await sb.auth.getUser();
        if (cancelled) return;
        bootResolved = true;
        clearTimeout(authTimeout);
        if (user && !error) {
          await acceptSession(user, intakeGeneration);
        } else {
          resolveUnauthenticatedIntake(intakeGeneration);
        }
      } catch {
        if (cancelled) return;
        try {
          const {
            data: {session},
          } = await sb.auth.getSession();
          if (cancelled) return;
          bootResolved = true;
          clearTimeout(authTimeout);
          if (session?.user) {
            await acceptSession(session.user, intakeGeneration);
            return;
          }
        } catch (_e) {
          /* fall through to signed-out state below */
        }
        bootResolved = true;
        clearTimeout(authTimeout);
        resolveUnauthenticatedIntake(intakeGeneration);
      }
    }

    bootAuth();

    // Moves session intake out of the auth callback and remembers what it
    // scheduled. src/app/boot/deferredAuthIntake.ts carries the full
    // explanation of the lock cycle this escapes.
    const authIntake = createDeferredAuthIntake();

    // NOT an async callback, deliberately. auth-js's own guidance is "Avoid
    // using async functions as callbacks" — it AWAITS this function from inside
    // its exclusive lock, so every statement below must be synchronous and must
    // not call another Supabase API. Anything that needs to is deferred.
    const {
      data: {subscription},
    } = sb.auth.onAuthStateChange((event, session) => {
      // Synchronous, no Supabase call: keeps the identity offline queueing
      // binds to when an expired token cannot refresh (authIdentityMemory.ts).
      rememberAuthIdentity(event, session);
      if (event === 'SIGNED_OUT') {
        // DROP QUEUED INTAKE FIRST, and unconditionally.
        //
        // A positive event can arrive while `sb.auth.signOut()` is still being
        // awaited: it reserves a generation and queues intake, and the manual
        // flag then makes this handler skip the session end — so nothing would
        // have invalidated that queued run before afterSignOut() cleared the
        // flag, and it would reopen the session the operator just left.
        //
        // Outside the branch on purpose: the queued run is equally wrong
        // whether the sign-out was manual or provider-signalled.
        authIntake.cancelAll();
        if (!manualSessionEndRef.current) {
          beginSessionEnd();
          appQueryClient.clear();
          finishSessionEnd();
        }
        // The SAME clear AuthContext.signOut performs, because this is the path
        // a session dies on WITHOUT anyone pressing the button: a revoked
        // refresh token (password changed on another device, an admin disabling
        // the account, a global sign-out elsewhere) or a token expired past its
        // lifetime on a barn tablet left idle for weeks.
        //
        // Without it the retention decision is only half-honoured. The query
        // keys carry the full subject scope either way, so the next user can
        // never READ the outgoing user's rows — but the stated invariant is that
        // they must not be RESIDENT on a shared device, and residency does not
        // care which path ended the session.
      }
      // Password-reset / invite link landed — hold the user on SetPasswordScreen
      // until they pick a password, then continue them into the app.
      if (event === 'PASSWORD_RECOVERY') {
        setPwRecovery(true);
      }
      // Refreshable sessions should hydrate from storage and survive token
      // refresh/user-update events without forcing a fresh login.
      if (['INITIAL_SESSION', 'SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event) && session?.user) {
        // Session intake is a SIDE EFFECT and must not run inside a state updater:
        // React may invoke an updater more than once for a transition (and does
        // under StrictMode), which would fire duplicate profile loads.
        //
        // It must not be DRIVEN by one either. Hoisting a flag out of an
        // updater — `let need = false; setAuthState(p => {need = ...; return p})`
        // — silently depends on React evaluating that updater synchronously.
        // React only does so as an eager bail-out optimisation when the fiber
        // has no pending work; with work queued the updater is deferred to the
        // render phase, the flag is still false when it is read, and profile
        // hydration is SKIPPED ENTIRELY. That fails worse than the duplicate
        // load it was meant to fix, and the updater is impure besides.
        //
        // Every intake path enters the coordinator. Duplicate events join the
        // exact same promise; subject replacement opens a new epoch before the
        // old profile/legacy run can commit again. A same-user event meeting an
        // already-settled failed run still JOINS it — reopening a settled
        // failure is the deferred P2 recovery lane, not this one.
        //
        // DEFERRED OUT OF THE CALLBACK, AND THIS IS THE LOCK ESCAPE.
        //
        // GoTrue awaits this callback from inside its exclusive lock. Intake
        // reaches the profile read, PostgREST asks supabase-js for an access
        // token, `_getAccessToken` calls `auth.getSession()`, and that re-enters
        // the same lock — a self-await with NO acquire timeout on the re-entrant
        // branch. The profile budget bounds the caller, so the app reaches
        // PROFILE_STATUS.FAILED instead of hanging; it does not resolve the
        // cycle. Returning from this callback synchronously is what breaks it.
        //
        // The handoff is a MACROTASK rather than a microtask because a
        // microtask, though it no longer hangs once the callback returns,
        // still runs while the lock is HELD and extends the critical section.
        // A macrotask runs after release, which is Supabase's own guidance.
        //
        // The generation is RESERVED synchronously, the instant the event is
        // received. Reading it instead would let an older boot/session
        // verification that settles first — with its own generation still
        // looking current — sign the user out and then invalidate this queued
        // intake, leaving a signed-out session on a valid login.
        const intakeGeneration = ++sessionIntakeGenerationRef.current;
        const user = session.user;
        authIntake.deferLatest(() => {
          void acceptSession(user, intakeGeneration);
        });
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearTimeout(authTimeout);
      // Intake is deferred now, so unmount can land between the schedule and
      // the fire. Dropping it is the same discipline `cancelled` and
      // `unsubscribe()` already apply to the paths that were always async.
      authIntake.cancelAll();
    };
  }, []);

  // Refresh Supabase session when tab becomes visible (prevents stale session after backgrounding)
  const lastVisibilityRefreshRef = React.useRef(0);
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible' && authState && authState.user) {
        const now = Date.now();
        if (now - lastVisibilityRefreshRef.current < AUTH_VISIBILITY_REFRESH_THROTTLE_MS) return;
        lastVisibilityRefreshRef.current = now;
        const intakeGeneration = sessionIntakeGenerationRef.current;
        sb.auth
          .refreshSession()
          .then(async function (res) {
            if (res?.data?.session?.user) {
              await acceptSession(res.data.session.user, intakeGeneration);
              return;
            }
            if (!res?.error) return;
            console.warn('Session refresh failed; preserving current login for retry.', res.error);
          })
          .catch(function (e) {
            console.warn('Session refresh failed; preserving current login for retry.', e);
          });
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return function () {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [authState]);

  // LEGACY BOOT COORDINATOR — historical duplicate-load defect, now superseded.
  // The former `loadUser`/singleFlight flow had two independent paths that
  // loaded the same identity on a cold boot:
  // All names and calls in the historical account below belonged to the
  // retired global-loader implementation; none is a live App data path.
  //
  //   * bootAuth() awaits sb.auth.getSession() and, on a stored session, calls
  //     loadUser(session.user);
  //   * the onAuthStateChange subscription — registered synchronously right
  //     after bootAuth() is *started* — receives INITIAL_SESSION for that same
  //     session and calls loadUser(session.user) as well.
  //
  // The subscription's `needsUserLoad` check reads authStateRef, but that ref
  // only updates once setAuthState has landed, which cannot happen until the
  // awaited profile fetch resolves. So on a cold boot the ref is still null
  // when INITIAL_SESSION arrives and the guard passes. The result is TWO
  // complete chains: two profile fetches AND — because loadUser ends with
  // `await loadAllData()` — two full boot data loads. Independent CI traces
  // observed exactly two chains on 8 of 8 boots.
  //
  // In that retired implementation, no downstream guard de-duplicated the
  // removed global loader's second invocation.
  //
  // Keying on user.id rather than a bare flag is deliberate: a genuine identity
  // CHANGE must still load. Same id + already in flight is the only case that
  // collapses, and the ref is cleared on settle so a later re-login or a real
  // user switch is unaffected.
  // Mirror publication is owned by maintenanceSourceLoad and the scheduled cron;
  // App never snapshots global context state for a client-side maintenance pass.
  function prepareSessionEpoch(lease: LegacyBootLease) {
    activeReleasedSurfaceClaimRef.current = null;
    lease.commit(() => {
      setSessionEpoch(lease.sessionEpoch);
      setSessionRunId(lease.runId);
      setProfileStatus(PROFILE_STATUS.RESOLVING);
    });
  }

  function acceptSession(user: SupabaseAuthUser, expectedIntakeGeneration: number | null = null) {
    // A getSession/visibility callback can resolve while the Header's manual
    // sign-out request is still in flight. It observed the outgoing cookie;
    // never let that stale intake reopen an epoch after beforeSignOut() closed
    // it. Provider SIGNED_OUT / AuthContext.afterSignOut own the terminal reset.
    if (manualSessionEndRef.current) return Promise.resolve(null);
    if (expectedIntakeGeneration !== null && expectedIntakeGeneration !== sessionIntakeGenerationRef.current) {
      return Promise.resolve(null);
    }
    sessionIntakeGenerationRef.current += 1;
    const start = legacyBootCoordinatorRef.current!.acceptSession(user, (lease) => loadUserImpl(user, lease));
    activeLegacyLeaseRef.current = start.lease;
    if (start.openedSession) prepareSessionEpoch(start.lease);
    return start.promise;
  }

  function claimReleasedSurface(
    lease: LegacyBootLease,
    resolvedAuthState: AuthState,
    surface = authenticatedSurfaceRef.current,
  ) {
    const claim = claimReleasedSurfaceBoundary({
      lease,
      authState: resolvedAuthState,
      view: surface.view,
      location: surface.location,
      showForm: surface.showForm,
      editId: surface.editId,
    });
    activeReleasedSurfaceClaimRef.current = claim;
    return claim;
  }

  function authMatchesLease(resolvedAuthState: AuthState, lease: LegacyBootLease | null) {
    return Boolean(
      lease &&
      typeof lease.userId === 'string' &&
      resolvedAuthState &&
      typeof resolvedAuthState === 'object' &&
      resolvedAuthState.user &&
      resolvedAuthState.user.id === lease.userId,
    );
  }

  // Route changes are fresh coordinator runs even for the same subject. This
  // revokes the previous released claim before a new exact route can mount.
  function publishReleasedSurfaceTransition(lease: LegacyBootLease) {
    lease.commit(() => {
      setSessionEpoch(lease.sessionEpoch);
      setSessionRunId(lease.runId);
    });
  }

  function enterReleasedSurface(resolvedAuthState: AuthState, surface: AuthenticatedSurface) {
    const current = activeLegacyLeaseRef.current;
    if (!current || !current.isCurrent() || !authMatchesLease(resolvedAuthState, current)) return false;

    const start = legacyBootCoordinatorRef.current!.retry({id: current.userId}, () => null);
    activeLegacyLeaseRef.current = start.lease;
    const releasedClaim = claimReleasedSurface(start.lease, resolvedAuthState, surface);
    if (!releasedClaim) return false;

    publishReleasedSurfaceTransition(start.lease);
    return true;
  }

  function beginSessionEnd() {
    sessionIntakeGenerationRef.current += 1;
    const end = legacyBootCoordinatorRef.current!.endSession();
    activeLegacyLeaseRef.current = null;
    activeReleasedSurfaceClaimRef.current = null;
    return end;
  }

  function finishSessionEnd() {
    const {sessionEpoch: endedEpoch} = legacyBootCoordinatorRef.current!.getState();
    setSessionEpoch(endedEpoch);
    setSessionRunId(0);
    setProfileStatus(PROFILE_STATUS.SIGNED_OUT);
    setAuthState(false);
    setPwRecovery(false);
  }

  function resolveUnauthenticatedIntake(intakeGeneration: number) {
    if (intakeGeneration !== sessionIntakeGenerationRef.current) return;
    if (legacyBootCoordinatorRef.current!.getCurrentLease()) {
      beginSessionEnd();
      appQueryClient.clear();
      finishSessionEnd();
      return;
    }
    setAuthState((prev) => (prev === null ? false : prev));
    setProfileStatus(PROFILE_STATUS.SIGNED_OUT);
  }

  // Header owns only the manual Supabase sign-out action. Register the
  // coordinator lifecycle here so manual and provider-signalled exits share
  // the exact same invalidate -> clear -> readiness-reset sequence.
  useEffect(() => {
    return registerSessionLifecycle({
      beforeSignOut() {
        manualSessionEndRef.current = true;
        beginSessionEnd();
      },
      afterSignOut() {
        finishSessionEnd();
        // TERMINAL RESERVATION, BEFORE THE FLAG IS RELEASED.
        //
        // beginSessionEnd() reserved when the sign-out started, which fences
        // intake queued BEFORE it. Intake that arrived DURING the awaited
        // sb.auth.signOut() reserved a NEWER generation than that, so it would
        // still look current here — and the line below is what would let it
        // through. Reserving once more makes every generation handed out during
        // the sign-out stale before the gate reopens.
        //
        // Ordered before the flag clear, not after: between the two there must
        // be no instant in which a stale generation is current AND the manual
        // guard is down.
        sessionIntakeGenerationRef.current += 1;
        manualSessionEndRef.current = false;
      },
    });
  }, [registerSessionLifecycle]);

  async function loadUserImpl(user: SupabaseAuthUser, lease: LegacyBootLease) {
    try {
      // The profile row is the session's privilege source, so a failed or
      // timed-out fetch must reach the fail-closed catch below — never resolve
      // to an assumed role. A missed fetch here silently DEMOTED admins and
      // ELEVATED light/inactive users to farm_team UI. Transient stalls are
      // real on slow connections, so retry the bounded fetch before failing.
      //
      // TRANSPORT ONLY, IN TWO PIECES. `readSessionProfile`
      // (src/lib/sessionProfileApi.ts) owns the PostgREST chain;
      // `loadSessionProfile` (src/app/boot/sessionProfileLoad.ts) owns the
      // attempt mechanics — one AbortController per attempt composed with this
      // lease's signal, ONE overall 20-second resolution budget, bounded caller
      // settlement, timer and listener cleanup, late-rejection parking, and
      // best-effort cancellation of each attempt. It replaces an un-aborted
      // promise race that left every timed-out attempt running against
      // PostgREST while the loop had already abandoned it.
      //
      // It is also the SOLE retry authority: readSessionProfile applies
      // `.retry(false)` so postgrest-js does not add a second, invisible retry
      // policy underneath this one.
      //
      // Everything that DECIDES anything about this profile stays HERE, in this
      // function, where tests/static/security_client_access_static.test.js reads
      // it out of App's own braced region: the `throw` on a failed outcome, and
      // the fail-closed catch's role literals and readiness setters.
      const outcome = await loadSessionProfile<LegacyBlob>({
        request: (signal) => readSessionProfile(sb, user, signal),
        isCurrent: () => lease.isCurrent(),
        leaseSignal: lease.signal,
      });
      // A superseded run publishes NOTHING — not a profile, and not a
      // fail-closed role either. Another lease owns the session now.
      if (outcome.status === 'superseded') return;
      if (outcome.status === 'failed') throw describeSessionProfileFailure(outcome);
      const profile: LegacyBlob = outcome.profile;
      // DEV-only role override for Playwright role-tier coverage. The
      // production bundle tree-shakes this `import.meta.env.DEV` branch
      // entirely; no prod surface exists. Spec sets
      // localStorage.setItem('wcf-test-role-override', 'farm_team') before
      // navigation; the override applies for the rest of that page session.
      let resolvedRole = profile.role || 'inactive';
      if (import.meta.env.DEV) {
        try {
          const override = window.localStorage.getItem('wcf-test-role-override');
          if (override && ['admin', 'management', 'farm_team', 'inactive', 'light'].includes(override)) {
            resolvedRole = override;
          }
        } catch {
          /* localStorage may be unavailable in some sandboxes */
        }
      }
      const resolvedAuthState = {user, role: resolvedRole, profile, name: profile.full_name || user.email};
      // Publish the resolved privilege source before deriving a route
      // disposition. A released surface must never mint its claim against a
      // pre-commit identity/route snapshot. The transition effect below is the
      // sole claimant once this same lease, epoch, and resolved AuthContext
      // state are visible to a committed render.
      if (
        !lease.commit(() => {
          setAuthState(resolvedAuthState);
          setProfileStatus(PROFILE_STATUS.RESOLVED);
        })
      ) {
        return;
      }
    } catch (e) {
      if (!lease.isCurrent()) return;
      console.error('loadUser error:', e);
      // HANDLED FAILURE, REPORTED. Until now this path's only output was the
      // console line above, so a profile failure in production left no trace in
      // Admin > Client Errors and the 2026-08-14 report had nothing to read.
      //
      // SANITIZED TWICE. The throw above is SYNTHESIZED by
      // describeSessionProfileFailure — an attempt count plus an allowlisted
      // code/name token, never the response body — so nothing derived from a
      // PostgREST payload reaches record_client_error by that route. Any OTHER
      // value reaching this catch still passes through the reporter's own
      // redaction (src/lib/clientErrorReporting.ts strips JWTs, Bearer headers,
      // supabase.co URLs and token query params) and its 200-char message cap.
      //
      // Fire-and-forget: reportError already swallows its own transport
      // failure, and a failing reporter must not stop the fail-closed commit.
      void reportError(SESSION_PROFILE_ERROR_SOURCE, e);
      // Fail closed: a PROFILE failure must NEVER elevate the role. Default to
      // least-privilege 'inactive' so admin/management-only surfaces stay
      // gated, and drop to home so a deep-linked sensitive view is not left
      // mounted under an unresolved profile.
      lease.commit(() => {
        setAuthState({user, role: 'inactive', name: user.email});
        setView('home');
        setProfileStatus(PROFILE_STATUS.FAILED);
      });
      return;
    }
    // Identity is resolved past this point. The committed readiness effect below
    // owns the route disposition; it never starts `loadAllData`, the retired
    // global loader. Keeping this profile query centralized here preserves the
    // existing retry and epoch behavior.
  }

  // The former global cold-boot shell is gone: route-local resources own every
  // authenticated data read, and malformed routes terminal-recover below.
  // The profile loader publishes resolved auth/readiness before this effect
  // classifies the initial route and all later route/Back/Forward/role-preview
  // transitions. It never guesses a release: only a current,
  // subject-matched, resolver-backed claim can mount the released renderer.
  // Every other candidate remains an explicit synchronization, redirect, or
  // terminal-recovery disposition.
  // Route recovery is explicit after the global loader's retirement. A route
  // can either claim a released renderer, wait for URL/view synchronization,
  // canonicalize a known safe spelling, or terminal-recover. It never triggers
  // a broad authenticated data load.
  useEffect(() => {
    const lease = activeLegacyLeaseRef.current;
    if (
      !lease ||
      !lease.isCurrent() ||
      profileStatus !== PROFILE_STATUS.RESOLVED ||
      sessionEpoch !== lease.sessionEpoch ||
      !authMatchesLease(authState, lease)
    ) {
      return;
    }

    const surface = {view, location, showForm, editId};
    const disposition = resolveAuthenticatedRouteDisposition({
      lease,
      authState,
      view: surface.view,
      location: surface.location,
      showForm: surface.showForm,
      editId: surface.editId,
    });
    if (disposition.kind === 'released') {
      const currentReleased = isCurrentReleasedSurfaceClaim(activeReleasedSurfaceClaimRef.current, {
        lease,
        authState,
        view: surface.view,
        location: surface.location,
        showForm: surface.showForm,
        editId: surface.editId,
      });
      if (!currentReleased) enterReleasedSurface(authState, surface);
      return;
    }

    activeReleasedSurfaceClaimRef.current = null;
    if (disposition.kind === 'redirect') {
      navigate(`${disposition.pathname}${location.search}${location.hash}`, {replace: true});
    }
    // sync-pending and terminal states deliberately have no loader side effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    location.pathname,
    location.search,
    location.hash,
    showForm,
    authState,
    profileStatus,
    sessionEpoch,
    sessionRunId,
    editId,
  ]);

  // The full admin profile directory is intentionally outside App/auth boot.
  // Its shared owner mounts only after an admin asks to open Users (or the
  // Webforms roster needs it), while loadUser above remains auth readiness.

  // `sbSave` is retired in migration 249: all 16 client-written app_store keys
  // now route through server operation RPCs (231, 235, 236, 237, 246, 249).

  // B2.2c: signOut moved to AuthContext, beside the session state it ends.

  // THE WRITE BASE IS TAKEN AT SAVE TIME, NOT AT RENDER TIME.
  //
  // `ppp-v4` is ONE app_store document holding the farm's WHOLE broiler batch
  // history — 30 rows and 36,006 bytes of JSON on production as of 2026-08-19 —
  // and this upsert replaces all of it. Every one of the eight callers builds
  // `nb` by mapping over `BatchesContext.batches`: `upd`'s autosave, `submit`
  // and `del` here, BatchForm's three document handlers, and BroilerListView's
  // two status flips. That array can be `[]` before a route-local action bridge
  // hydrates it, or after a local read failure, so each expression can collapse
  // to an empty — or one-element — array. This function used to upsert that
  // result verbatim over the history. There is no migration ledger on production
  // to roll such a write back from.
  //
  // So the base is READ from the server here and `nb` is merged onto it: `nb`
  // stays authoritative for every row it names, and rows it never mentioned are
  // carried through as the SAME objects the read produced. When the read agrees
  // with the caller's local action state, `plan.next` IS `nb`, element for
  // element, so the ordinary hydrated save is unchanged by this.
  //
  // `removed` is the rows a caller deliberately dropped. It is passed as ROWS
  // rather than ids so the caller cannot name a removal by an identity the
  // stored row does not carry, and it is what makes the non-clobbering
  // STRUCTURAL: a row this function was never handed cannot be deleted, so a
  // future caller cannot lose one by forgetting to preserve it. `del` below is
  // the only caller that removes anything.
  //
  // See src/lib/broilerBatchesApi.ts for the merge and its four refusals, and
  // tests/app_broiler_batches_write_base_boot_independence.test.js, which runs
  // THIS body against an empty boot array and a stocked server.
  //
  // MIGRATION 236: NOTHING WRITES THE WHOLE ARRAY ANY MORE. The merged plan
  // still decides what the caller wants every named row to become, and still
  // refuses exactly as before; then planBroilerBatchOperations turns the rows
  // the caller actually CHANGED into audited server operations
  // (broiler_apply_operation), each with its expected-before values, so two
  // editors' disjoint changes both land and a stale copy is never written back.
  // See src/lib/broilerBatchOperationApi.ts for the field rule.
  async function persist(
    nb: unknown[],
    removed: readonly unknown[] = [],
    documents: BroilerBatchDocumentsPatch | null = null,
  ) {
    broilerSaveFailure.current = null;
    const read: {fresh: FreshBroilerBatchesRead | null} = {fresh: null};
    const plan = await planBroilerBatchesSave(
      async () => (read.fresh = await readFreshBroilerBatches(sb)),
      nb,
      removed,
      documents,
    );
    if (plan.kind !== 'save') {
      // FAIL CLOSED. Nothing is written and no mirror is republished: there is
      // no base, and every blob this could write instead would delete batch
      // history it never read. The operator gets the same `error` indicator a
      // failed `sbSave` has always driven, so a refused save is retried rather
      // than mistaken for a saved one.
      reportBroilerBatchesRefusal(plan.reason, setSaveStatus);
      return false;
    }
    const fresh = read.fresh;
    const freshRows = fresh && fresh.ok && Array.isArray(fresh.rows) ? (fresh.rows as unknown[]) : null;
    const ops = planBroilerBatchOperations({
      fresh: freshRows,
      shown: freshRows
        ? applyBroilerBootRepair(freshRows as Parameters<typeof applyBroilerBootRepair>[0], feedCosts).batches
        : null,
      base: broilerRowsRef.current,
      next: plan.next,
      removed,
      documents,
    });
    if (!ops.ok) {
      reportBroilerBatchesRefusal(ops.reason === 'unkeyed' ? 'next-unkeyed' : 'deleted-elsewhere', setSaveStatus);
      return false;
    }
    if (ops.operations.length > 0) {
      setSaveStatus('saving');
      const run = await runBroilerBatchOperations(sb, ops.operations);
      if (!run.ok) {
        broilerSaveFailure.current = describeBroilerSaveFailure(run);
        reportBroilerBatchesRefusal(run.reason === 'conflict' ? 'conflict' : 'operation-failed', setSaveStatus);
        // Whatever DID land is real: republish the server copy so the next
        // save plans from it rather than from this caller's array.
        await publishConfirmedBroilerBatches(nb, false);
        return false;
      }
    }
    await publishConfirmedBroilerBatches(nb, ops.operations.length > 0);
    return true;
    // The session-free maintenance cron owns the six derived webform_config
    // mirrors after legacy-loader retirement. It re-reads ppp-v4 and the
    // companion source rows when it runs, so this browser write deliberately
    // publishes no mirror snapshot from a possibly stale context closure.
    // `plan.next`, rather than `nb`, is still the canonical source value: when
    // the local mirror is empty, it retains the server rows the caller never named.
  }
  // THE DOCUMENT SAVE, NARROWED TO THE ROW IT EDITS.
  //
  // src/broiler/BatchForm.tsx used to do this itself, three times over:
  //
  //     const nb = batchRows.map((b) => (b.id === editId ? {...b, documents} : b));
  //     setBatches(nb); persist(nb);
  //
  // `batchRows` was `useBatches().batches` — App's former ppp-v4 mirror — and
  // holding it was the entire content of `legacy.context.batches.batches` on
  // that component. Nothing rendered from it; it existed to be mapped over.
  // So the form hands up the id and the new document list, and this closure
  // owns the array, which it already did for `upd`, `submit` and `del`.
  //
  // The confirmed merged document is published by persist after the write.
  // A failed save leaves the caller's attachment draft available to retry.
  //
  // The SAVE is the third argument. `persist` merges onto a save-time read, and
  // the patch is applied to that MERGE — so the row is found whether it came
  // from `nb` or was carried in from the server, and the document lands even
  // when the local mirror is empty instead of being uploaded to storage and then
  // dropped from the blob in silence. That is the half the old expression could
  // not do at all, and it is proved as a scenario in
  // tests/app_broiler_batch_documents_boot_independence.test.js.
  // The confirmed server copy, published after a broiler save: App's mirror,
  // the hub resource every released broiler route renders, and the Timeline
  // read -- what sbSave used to do for ppp-v4 after a whole-array write. The
  // rows are re-read rather than assembled here, so they are exactly what the
  // server holds (other editors' changes included). A failed re-read falls
  // back to the caller's array, as the screen showed it before 236.
  async function publishConfirmedBroilerBatches(callerRows: unknown[], wrote: boolean) {
    const after = await readFreshBroilerBatches(sb);
    const rows = after.ok && Array.isArray(after.rows) ? (after.rows as unknown[]) : null;
    const confirmed = rows
      ? applyBroilerBootRepair(rows as Parameters<typeof applyBroilerBootRepair>[0], feedCosts).batches
      : callerRows;
    // An UNBOOTED mount keeps publishing only the caller's own array, as before
    // 236: a save must not hand a released-surface frame the server's rows
    // (that is the boot's job). A hydrated mirror gets the confirmed copy.
    const hydrated = Array.isArray(broilerRowsRef.current) && broilerRowsRef.current.length > 0;
    const published = hydrated ? confirmed : callerRows;
    broilerRowsRef.current = published as unknown[];
    setBatches(published);
    if (rows) {
      appQueryClient.setQueriesData({queryKey: [RESOURCE.BROILER_BATCHES_HUB]}, (current: unknown) => {
        if (!current || typeof current !== 'object' || Array.isArray(current)) return current;
        const hub = current as Record<string, unknown>;
        return {
          ...hub,
          batches: applyBroilerBootRepair(rows as Parameters<typeof applyBroilerBootRepair>[0], hub.feedCosts).batches,
        };
      });
    }
    if (wrote) {
      void appQueryClient.invalidateQueries({queryKey: [RESOURCE.BROILER_BATCHES_HUB]});
      void appQueryClient.invalidateQueries({queryKey: [RESOURCE.BROILER_TIMELINE_CORE]});
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2500);
    }
  }
  async function persistDocuments(batchId: string | null, documents: unknown[]): Promise<boolean> {
    const nb = batches.map((b) => (b.id === batchId ? {...b, documents} : b));
    return persist(nb, [], {batchId, documents});
  }
  // `persistBreeding` and `persistFarrowing` USED TO LIVE HERE, and they are
  // gone rather than merely unused. Each was a bare
  // `sbSave('ppp-<blob>-v1', nb)` threaded through PigEntry to one route, and
  // each was called beside a PigContext setter that kept a mirror in step for
  // readers that no longer exist: NOTHING in src/ destructures `farrowingRecs`
  // off usePig() any more, and `breedingCycles` was read only by the
  // `resolveSire` closure that used to sit further down this file, which both
  // of its consumers had already stopped calling.
  // /pig/breeding and /pig/farrowing write their own blob through
  // src/lib/appStoreBlobApi.ts now, which is what empties
  // `legacy.context.pig.breeding` and `legacy.context.pig.farrowing` in
  // src/app/data/surfaceDataManifest.ts. `persistFeeders` USED TO STAY here
  // after them; it is gone too (migration 237, B4c). Nothing called it any
  // more -- PigEntry declared it but never destructured it -- and it was the
  // last whole-document ppp-feeders-v1 writer in the browser: every feeder
  // write is a 231/235/237 server operation, which is what the direct-write
  // closure (238) needs.
  // `persistLayerHousings` USED TO LIVE HERE, and it is gone rather than merely
  // unused. It was `setLayerHousings(nb)` plus a `layer-housings-save`
  // maintenance pass — App's POST-write fan-out, never a write of its own; it
  // had no `sbSave`, unlike its `persistLayerGroups` sibling above. Its only
  // consumers were the two layer record pages. Both halves are now redundant:
  // those pages own and revalidate their record resources, and no route
  // transition repopulates an App-wide layer directory. See
  // src/app/data/migratedSurfaces/layer.batches.detail.ts.
  //
  // `setLayerHousings` itself STAYS as LayerContext's local mutation helper.
  // It is not populated by a global loader and does not make any released
  // surface depend on an App-wide layer mirror.
  // `persistBreeders`, `persistBreedOptions` and `persistOriginOptions` USED TO
  // LIVE HERE. All three were bare `sbSave` calls on one route's three lists,
  // declared as ONE residual because no release could take one without the
  // others; /pig/sows writes all three through src/lib/appStoreBlobApi.ts now.
  // What it KEPT is `setBreeders`: it remains an action-time compatibility
  // mirror for save flows that may derive `active_groups` from the current
  // registry. A context setter is not a read or a boot dependency, so it does
  // not hold the surface partial; retaining it prevents a stale derived group
  // snapshot after an edit.

  const broilerBusy = React.useRef(false);
  const broilerClosing = React.useRef(false);
  const newBroilerId = React.useRef<string | null>(null);
  // 236: the rows the last broiler save confirmed (or the mirror, when it
  // changes from elsewhere). It is the `base` a save diffs against, held in a
  // ref so a save that starts before the previous one's re-render still sees
  // what that save confirmed, instead of mistaking its own change for someone
  // else's.
  const broilerRowsRef = React.useRef<unknown[] | null>(null);
  React.useLayoutEffect(() => {
    broilerRowsRef.current = batches as unknown[];
  }, [batches]);
  // Why the last broiler save failed, in words the form can show.
  const broilerSaveFailure = React.useRef<string | null>(null);
  // The name the server holds for a batch. The autosave never renames (a
  // rename cascades to every daily report, weigh-in and task, and must not run
  // on each pause in typing); Done carries the new name through submit.
  function savedBroilerName(id: unknown): string | null {
    const row = (broilerRowsRef.current || []).find((b) => !!b && (b as {id?: unknown}).id === id) as
      | {name?: unknown}
      | undefined;
    return row && typeof row.name === 'string' ? row.name : null;
  }
  async function persistBroilerDraft({id, form: f}: {id: unknown; form: typeof form}) {
    // Save boundary: never autosave an invalid brooder length; submit and
    // closeForm refuse it out loud.
    if (isInvalidBrooderDays(f.brooderDays)) return false;
    const tl = calcTimeline(f.hatchDate, f.breed, f.processingDate, f.growoutWeeks, f.brooderDays);
    // Parse numeric form fields back to numbers before persisting (form state holds raw strings during typing)
    const numFields = [
      'birdCount',
      'birdCountActual',
      'brooderFeedLbs',
      'schoonerFeedLbs',
      'gritLbs',
      'mortalityCumulative',
      'week4Lbs',
      'week6Lbs',
      'perLbStandardCost',
      'perLbStarterCost',
      'perLbGritCost',
      'totalToProcessor',
      'processingCost',
      'avgBreastLbs',
      'avgThighsLbs',
      'avgDressedLbs',
      'totalLbsWhole',
      'totalLbsCuts',
    ];
    const fNum = {...f, brooderDays: validBrooderDays(f.brooderDays)};
    for (const k of numFields) {
      const v = fNum[k];
      fNum[k] = v === '' || v == null ? 0 : parseFloat(v) || 0;
    }
    const computedStatus = calcPoultryStatus({...fNum, ...tl});
    // When a batch is active and has no rates yet (e.g. just transitioned from planned),
    // stamp the current global admin feed cost rates onto it.
    if (computedStatus === 'active' && (!fNum.perLbStarterCost || !fNum.perLbStandardCost)) {
      fNum.perLbStarterCost = feedCosts.starter || 0;
      fNum.perLbStandardCost = feedCosts.grower || 0;
      fNum.perLbGritCost = feedCosts.grit || 0;
    }
    const batch = {
      id,
      ...fNum,
      ...tl,
      brooderIn: resolveBrooderEntryDate(fNum.hatchDate, fNum.brooderIn),
      status: computedStatus,
    };
    // 236: the autosave keeps the saved name; Done renames (see savedBroilerName).
    const heldName = savedBroilerName(id);
    if (heldName !== null) batch.name = heldName;
    const nb = batches.map((b) => (b.id === id ? batch : b));
    nb.sort((a, b) => (a.hatchDate || '').localeCompare(b.hatchDate || ''));
    if (!(await persist(nb))) {
      setFormNotice({
        kind: 'error',
        message: broilerSaveFailure.current || 'Not Saved. Your edits are still here. Retry with Done.',
      });
      return false;
    }
    // A held-back rename stays visible as unsaved, so closing the form saves it.
    setOriginalForm(heldName !== null ? {...f, name: heldName} : f);
    return true;
  }
  const broilerPersistRef = React.useRef(persistBroilerDraft);
  broilerPersistRef.current = persistBroilerDraft;
  const [broilerAutosave] = React.useState(() =>
    createConfirmedDraftAutosave<{id: unknown; form: typeof form}>({
      debounceMs: 1500,
      validate: ({form: candidate}) => !!candidate.name.trim() && !isInvalidBrooderDays(candidate.brooderDays),
      persist: (candidate) => broilerPersistRef.current(candidate),
      onState: () => {},
    }),
  );
  React.useLayoutEffect(() => {
    broilerAutosave.resume();
    return () => broilerAutosave.suspend();
  }, [broilerAutosave]);
  useAppDraftGuard({
    ownerKey: 'broiler-app-draft',
    isUnsafeToLeave: () => broilerAutosave.isDirty() || broilerBusy.current,
    settleBeforeLeave: () => (broilerBusy.current ? Promise.resolve(false) : broilerAutosave.flush()),
  });
  // Form field update
  function upd(k: string, v: unknown) {
    if (broilerBusy.current || broilerClosing.current) return;
    const f = {...form, [k]: v};
    if (k === 'breed') {
      // Hatchery is decoupled from breed — any hatchery can supply any breed now.
      // (Previously locked WR to Freedom Ranger Hatchery; that's no longer enforced.)
    }
    // Selecting a hatchery stamps its growout length (So Big Farms: 8 weeks) or
    // clears it back to the breed default. Booked dates are never moved.
    if (k === 'hatchery') f.growoutWeeks = hatcheryGrowoutWeeks(v as string);
    // Input boundary: only a whole 14-18 day brooder length is kept.
    if (k === 'brooderDays') f.brooderDays = validBrooderDays(v);
    if (k === 'schooner') f.birdCount = v === '1' ? 650 : 750;
    // Follow hatch edits only for a blank/default entry; keep recorded entry overrides.
    if (k === 'hatchDate' && (!resolveBrooderEntryDate('', f.brooderIn) || f.brooderIn === form.hatchDate)) {
      f.brooderIn = resolveBrooderEntryDate(f.hatchDate);
    }
    setForm(f);
    try {
      setConflicts(detectConflicts(f, batches, layerBatches, editId));
    } catch (e) {
      console.warn('detectConflicts failed on field change:', e);
      setConflicts([]);
    }
    setOverride(false);
    // Debounced auto-save: 1.5s after last keystroke
    if (editId) {
      clearTimeout(autoSaveTimer.current);
      broilerAutosave.update({id: editId, form: f});
    }
  }

  function openAdd() {
    newBroilerId.current = null;
    setFormNotice(null);
    setForm(EMPTY_FORM);
    setEditId(null);
    setConflicts([]);
    setOverride(false);
    setShowLegacy(false);
    setOriginalForm(EMPTY_FORM);
    setShowForm(true);
  }
  function openEdit(b: LegacyBlob) {
    const isB24 = /^b-24-/i.test(b.name);
    let brooderFeedLbs = b.brooderFeedLbs || 0;
    let schoonerFeedLbs = b.schoonerFeedLbs || 0;
    let gritLbs = b.gritLbs || 0;
    let mortalityCumulative = b.mortalityCumulative || 0;
    if (!isB24 && broilerDailys.length > 0) {
      const bd = broilerDailys.filter(
        (d) =>
          (d.batch_label || '')
            .toLowerCase()
            .trim()
            .replace(/^\(processed\)\s*/, '')
            .trim() === b.name.toLowerCase().trim(),
      );
      if (bd.length > 0) {
        brooderFeedLbs = Math.round(
          bd.filter((d) => d.feed_type === 'STARTER').reduce((s, d) => s + (parseFloat(d.feed_lbs) || 0), 0),
        );
        schoonerFeedLbs = Math.round(
          bd.filter((d) => d.feed_type === 'GROWER').reduce((s, d) => s + (parseFloat(d.feed_lbs) || 0), 0),
        );
        gritLbs = Math.round(bd.reduce((s, d) => s + (parseFloat(d.grit_lbs) || 0), 0));
        mortalityCumulative = bd.reduce((s, d) => s + (parseInt(d.mortality_count) || 0), 0);
      }
    }
    // Blank entries default to hatch day; recorded entry overrides remain intact.
    const brooderIn = resolveBrooderEntryDate(b.hatchDate, b.brooderIn);
    setForm({
      name: b.name,
      breed: b.breed || '',
      hatchery: b.hatchery || '',
      hatchDate: b.hatchDate || '',
      birdCount: b.birdCount || 750,
      birdCountActual: b.birdCountActual || '',
      brooder: b.brooder || '1',
      schooner: b.schooner || '2&3',
      processingDate: b.processingDate || '',
      status: b.status || 'planned',
      notes: b.notes || '',
      brooderIn,
      brooderOut: b.brooderOut || '',
      brooderFeedLbs,
      schoonerFeedLbs,
      gritLbs,
      mortalityCumulative,
      week4Lbs: b.week4Lbs || 0,
      week6Lbs: b.week6Lbs || 0,
      perLbStandardCost: b.perLbStandardCost || 0,
      perLbStarterCost: b.perLbStarterCost || 0,
      perLbGritCost: b.perLbGritCost || 0,
      chickCost: b.chickCost || 0,
      totalToProcessor: b.totalToProcessor || 0,
      processingCost: b.processingCost || 0,
      avgBreastLbs: b.avgBreastLbs || 0,
      avgThighsLbs: b.avgThighsLbs || 0,
      avgDressedLbs: b.avgDressedLbs || 0,
      totalLbsWhole: b.totalLbsWhole || 0,
      totalLbsCuts: b.totalLbsCuts || 0,
      documents: b.documents || [],
      growoutWeeks: b.growoutWeeks,
      brooderDays: b.brooderDays,
    });
    const snap = {
      name: b.name,
      breed: b.breed || '',
      hatchery: b.hatchery || '',
      hatchDate: b.hatchDate || '',
      birdCount: b.birdCount || 750,
      birdCountActual: b.birdCountActual || '',
      brooder: b.brooder || '1',
      schooner: b.schooner || '2&3',
      processingDate: b.processingDate || '',
      status: b.status || 'planned',
      notes: b.notes || '',
      brooderIn,
      brooderOut: b.brooderOut || '',
      brooderFeedLbs: b.brooderFeedLbs || 0,
      schoonerFeedLbs: b.schoonerFeedLbs || 0,
      gritLbs: b.gritLbs || 0,
      mortalityCumulative: b.mortalityCumulative || 0,
      week4Lbs: b.week4Lbs || 0,
      week6Lbs: b.week6Lbs || 0,
      perLbStandardCost: b.perLbStandardCost || 0,
      perLbStarterCost: b.perLbStarterCost || 0,
      perLbGritCost: b.perLbGritCost || 0,
      chickCost: b.chickCost || 0,
      totalToProcessor: b.totalToProcessor || 0,
      processingCost: b.processingCost || 0,
      avgBreastLbs: b.avgBreastLbs || 0,
      avgThighsLbs: b.avgThighsLbs || 0,
      avgDressedLbs: b.avgDressedLbs || 0,
      totalLbsWhole: b.totalLbsWhole || 0,
      totalLbsCuts: b.totalLbsCuts || 0,
      growoutWeeks: b.growoutWeeks,
      brooderDays: b.brooderDays,
    };
    setOriginalForm(snap);
    // Auto-enable Show Legacy if editing a batch that already has a legacy breed or hatchery selected,
    // so the user can actually see the saved value in the dropdown.
    const hasLegacyBreed = LEGACY_BREEDS.some((lb) => lb.code === b.breed);
    const hasLegacyHatchery = LEGACY_HATCHERIES.includes(b.hatchery);
    setShowLegacy(hasLegacyBreed || hasLegacyHatchery);
    // Compute existing conflicts on open so already-conflicting batches show their warnings immediately.
    // Wrapped in try/catch so a malformed batch record can never block the form from opening.
    setEditId(b.id);
    try {
      setConflicts(
        detectConflicts(
          // `name` is not part of ConflictForm; detectConflicts ignores it. The
          // assertion keeps the extra key (and the call) exactly as written
          // rather than editing the argument.
          {
            name: b.name,
            breed: b.breed || '',
            hatchDate: b.hatchDate || '',
            brooder: b.brooder || '1',
            schooner: b.schooner || '2&3',
            processingDate: b.processingDate || '',
            growoutWeeks: b.growoutWeeks,
            brooderDays: b.brooderDays,
          } as ConflictForm,
          batches,
          layerBatches,
          b.id,
        ),
      );
    } catch (e) {
      console.warn('detectConflicts failed on openEdit:', e);
      setConflicts([]);
    }
    setFormNotice(null);
    setOverride(false);
    setShowForm(true);
  }

  // B2.3: the workbook reader moved to src/lib/processorXlsx.js. What is
  // left here is the part that is genuinely App's -- turning a parse
  // result into the notice the broiler batch form shows. Both notice
  // bodies below are the original text, unedited.
  async function parseProcessorXlsx(file: File) {
    const result = await parseProcessorWorkbook(file);
    if (result.status === 'ok') {
      setParsedProcessor(result.parsed);
      return;
    }
    if (result.status === 'loader-error') {
      setFormNotice({
        kind: 'error',
        message: 'Could not load Excel parser. Check your internet connection and try again.',
      });
      return;
    }
    if (result.status === 'error') {
      setFormNotice({kind: 'error', message: 'Excel parse error: ' + result.message});
      return;
    }
    setFormNotice({
      kind: 'warning',
      message:
        "Couldn't find processor data in " +
        file.name +
        '.\n\nThe parser looks for either:\n  • A raw per-package sheet with "Description" and "Weight" columns (Sonny\'s standard format), or\n  • A pivot summary with "Row Labels" / "Total Weight" / "Average Weight" columns.\n\nThe file was uploaded as an attachment.',
    });
  }

  // B2.2c: the two confirmation overlays, their state, their openers and the
  // window._wcfConfirmDelete / window._wcfConfirm globals all moved to
  // src/contexts/ConfirmContext.jsx, which renders them above the app content.
  // App reads the openers from useConfirm() at the top of this component.
  //
  // The App-bound header wrapper went with them. It existed
  // only to thread App-owned props into HeaderBase while keeping ONE component
  // identity across App re-renders -- a fresh arrow function per render
  // remounted HeaderBase and discarded open overlay state. Header takes no
  // props at all now, so views import the module directly and its identity is
  // constant by construction rather than by a ref. The failure mode that
  // wrapper defended against cannot recur through a module import.

  async function closeForm(): Promise<boolean> {
    if (broilerBusy.current || broilerClosing.current) return false;
    broilerClosing.current = true;
    try {
      const hadPending = broilerAutosave.isDirty();
      if (!(await broilerAutosave.flush())) {
        // A refused drain is either a failed persist (persistBroilerDraft
        // already surfaced 'Not Saved') or an invalid newest revision — name
        // that reason out loud when nothing else has, so a close never fails
        // silently. upd keeps `form` and the pending revision identical, so
        // the draft is the right thing to check here.
        if (isInvalidBrooderDays(form.brooderDays)) {
          setFormNotice({kind: 'error', message: 'Days in brooder must be a whole number from 14 to 18.'});
        } else if (!form.name.trim()) {
          setFormNotice({kind: 'error', message: 'Please enter a batch name.'});
        }
        return false;
      }
      // flush drains the exact newest revision. This render's originalForm
      // still predates its asynchronous setter and must not trigger a second write.
      // A rename the autosave held back (236) is the exception: it is saved by
      // submit below, as one cascading rename.
      const renamePending =
        !!editId && savedBroilerName(editId) !== null && form.name.trim() !== savedBroilerName(editId);
      if (renamePending) {
        const ok = await submit(false);
        return ok !== false;
      }
      if (hadPending) {
        setShowForm(false);
        return true;
      }
      clearTimeout(autoSaveTimer.current);
      setFormNotice(null);
      if (editId && originalForm) {
        const keys = [
          'name',
          'breed',
          'hatchery',
          'hatchDate',
          'birdCount',
          'birdCountActual',
          'brooder',
          'schooner',
          'processingDate',
          'status',
          'notes',
          'brooderIn',
          'brooderOut',
          'brooderFeedLbs',
          'schoonerFeedLbs',
          'gritLbs',
          'mortalityCumulative',
          'week4Lbs',
          'week6Lbs',
          'perLbStandardCost',
          'perLbStarterCost',
          'perLbGritCost',
          'totalToProcessor',
          'processingCost',
          'avgBreastLbs',
          'avgThighsLbs',
          'avgDressedLbs',
          'totalLbsWhole',
          'totalLbsCuts',
          'growoutWeeks',
          'brooderDays',
        ];
        const changed = keys.some((k) => String(form[k] || '') !== String(originalForm[k] || ''));
        if (changed) {
          const ok = await submit(false);
          // submit() refused (blank name / hard conflicts) — keep the form
          // open and surface formNotice. Callers (BroilerBatchPage.handleClose)
          // must check this return value before navigating away.
          return ok !== false;
        }
        setShowForm(false);
        return true;
      }
      setShowForm(false);
      return true;
    } finally {
      broilerClosing.current = false;
    }
  }

  async function submit(force: boolean): Promise<boolean> {
    if (broilerBusy.current) return false;
    broilerBusy.current = true;
    try {
      // Validate BEFORE draining the autosave. A drain refusal would otherwise
      // return silently when the newest revision is invalid (a stored bad
      // brooder length, a blank name); these checks refuse out loud first,
      // which is the contract the form has always had. upd keeps `form` and
      // the pending revision identical, so validating the draft is validating
      // what the flush would persist.
      setFormNotice(null);
      if (!form.name.trim()) {
        setFormNotice({kind: 'error', message: 'Please enter a batch name.'});
        return false;
      }
      if (isInvalidBrooderDays(form.brooderDays)) {
        setFormNotice({kind: 'error', message: 'Days in brooder must be a whole number from 14 to 18.'});
        return false;
      }
      // Block on hard conflicts unless force=true. Soft (layer) conflicts always pass.
      const hardConflicts = (conflicts || []).filter((c) => !c.soft);
      if (hardConflicts.length > 0 && !force) {
        setFormNotice({
          kind: 'error',
          message: 'There are scheduling conflicts. Use the Override & Save Anyway button if you really want to save.',
        });
        return false;
      }
      // Flush drains the exact newest revision. This render's originalForm
      // still predates its asynchronous setter and must not trigger a second
      // write; a false here is a refused persist (already surfaced by
      // persistBroilerDraft) or a suspended session, never a validation stop.
      if (!(await broilerAutosave.flush())) return false;
      const tl = calcTimeline(form.hatchDate, form.breed, form.processingDate, form.growoutWeeks, form.brooderDays);
      // Parse numeric form fields back to numbers (form state holds raw strings during typing)
      const numFields = [
        'birdCount',
        'birdCountActual',
        'brooderFeedLbs',
        'schoonerFeedLbs',
        'gritLbs',
        'mortalityCumulative',
        'week4Lbs',
        'week6Lbs',
        'perLbStandardCost',
        'perLbStarterCost',
        'perLbGritCost',
        'totalToProcessor',
        'processingCost',
        'avgBreastLbs',
        'avgThighsLbs',
        'avgDressedLbs',
        'totalLbsWhole',
        'totalLbsCuts',
      ];
      const formNum = {...form, brooderDays: validBrooderDays(form.brooderDays)};
      for (const k of numFields) {
        const v = formNum[k];
        formNum[k] = v === '' || v == null ? 0 : parseFloat(v) || 0;
      }
      const computedStatus = calcPoultryStatus({...formNum, ...tl});
      // When a batch is active and has no rates yet (e.g. just transitioned from planned),
      // stamp the current global admin feed cost rates onto it.
      if (computedStatus === 'active' && (!formNum.perLbStarterCost || !formNum.perLbStandardCost)) {
        formNum.perLbStarterCost = feedCosts.starter || 0;
        formNum.perLbStandardCost = feedCosts.grower || 0;
        formNum.perLbGritCost = feedCosts.grit || 0;
      }
      // conflictOverride auto-clears when there's no longer a hard conflict.
      // The flag was sticky before — it stayed true after dates were edited
      // to resolve the conflict, leaving stale ⚠ warnings on processed batches.
      const stillConflicting = hardConflicts.length > 0;
      const batch = {
        id: editId || (newBroilerId.current ??= String(Date.now())),
        ...formNum,
        ...tl,
        brooderIn: resolveBrooderEntryDate(formNum.hatchDate, formNum.brooderIn),
        status: computedStatus,
        conflictOverride: stillConflicting ? force || form.conflictOverride || false : false,
      };
      const nb = editId ? batches.map((b) => (b.id === editId ? batch : b)) : [...batches, batch];
      nb.sort((a, b) => (a.hatchDate || '').localeCompare(b.hatchDate || ''));
      const confirmed = await persist(nb);
      if (!confirmed) {
        setFormNotice({
          kind: 'error',
          message: broilerSaveFailure.current || 'Not Saved. Your answers are still here. Retry the save.',
        });
        return false;
      }
      newBroilerId.current = null;
      setOriginalForm(null);
      setShowForm(false);
      setOverride(false);
      return true;
    } finally {
      broilerBusy.current = false;
    }
  }

  function del(id: string, onDeleted?: () => void) {
    confirmDelete('Delete this batch? This cannot be undone.', async () => {
      if (broilerBusy.current) return;
      broilerBusy.current = true;
      try {
        if (!(await broilerAutosave.flush())) return;
        const removed = batches.find((b) => b.id === id) || null;
        const nb = batches.filter((b) => b.id !== id);
        // The ONLY caller that drops a row, and therefore the only one that hands
        // `persist` a removal. Anything not named here is carried through from the
        // server copy, so a delete pressed while the local mirror is empty removes
        // nothing instead of emptying the blob — the row cannot be found, so it
        // cannot be named, so it is not deleted.
        const confirmed = await persist(nb, removed ? [removed] : []);
        if (!confirmed) {
          setFormNotice({kind: 'error', message: 'Not Deleted. Please retry.'});
          return;
        }
        if (editId === id) {
          setShowForm(false);
          setEditId(null);
          setOriginalForm(null);
        }
        // The broiler.batch Activity row is written by broiler_apply_operation
        // in the delete's own transaction (236), keyed by the batch id.
      } finally {
        broilerBusy.current = false;
      }
      // Run after the busy guard clears and only after confirmed deletion.
      onDeleted?.();
    });
  }

  // Timeline-helper derived values (tlS/tlE/totalDays/pct/wkHdrs) + BatchForm
  // derived values (tl/targetHatch/hatchSuggestions/hatchWarn/procWarn/
  // hatcheries) + dead `counts` reducer removed — the timeline ones were only
  // consumed by the extracted BroilerTimelineView (which recomputes its own),
  // the form ones by the soon-to-be-extracted BatchForm (same treatment),
  // and `counts` had no consumers at all after the home extraction.

  // ── HEADER ──
  // B2.2c: <Header /> is a plain module import with no props. Every value it
  // used to be handed now comes from where it is owned: sb from
  // src/lib/supabase.js, signOut from AuthContext, the opt-in user directory
  // from its shared provider, the two access predicates from src/lib/globalNav.js,
  // and the confirmation overlays from ConfirmContext, which renders them itself.

  // ── AUTH GATES ──
  // Lane 1 CP1: the public report/form surfaces (webformhub, tasksWebform,
  // addfeed, weighins, webform, fuelingHub, fuelSupply) used to render in a
  // bypass block ABOVE these gates so anonymous operators could submit. They
  // are now login-required and render BELOW the gates (see "REPORT/FORM
  // SURFACES (login required)" further down). URLs and aliases are unchanged.
  // SetPasswordScreen comes first: a recovery / invite link gives the user a
  // valid session, so authState would otherwise jump straight to home.
  if (pwRecovery)
    return (
      <SetPasswordScreen
        prefilledEmail={authState && authState.user ? authState.user.email : null}
        onDone={() => setPwRecovery(false)}
      />
    );

  // ── PUBLIC NEWSLETTER (no auth — renders ABOVE the login gate) ──
  // The Monthly Review archive is a public, no-login web surface. It mounts
  // before the authState gates so a logged-out visitor never hits LoginScreen,
  // and it talks ONLY to the three anon newsletter RPCs (no app data, no
  // contexts). The admin/build surface stays admin-only below (view
  // 'newsletterAdmin' at /admin/newsletter). Keyed on pathname so it paints
  // immediately, before auth resolves. /newsletter exact + /newsletter/<...>
  // sub-paths; /admin/newsletter is NOT matched (different prefix).
  if (location.pathname === '/newsletter' || location.pathname.startsWith('/newsletter/')) {
    return React.createElement(NewsletterPublicApp);
  }

  // F05/C20: the one branded splash (brand-dark, wordmark, thin bar) while auth
  // resolves. Static presentation only — no data read, no route dispatch.
  if (authState === null) return <BootSplash />;
  if (authState === false) return <LoginScreen />;

  // DEACTIVATED ACCOUNT (migration 218) — before any route renderer. A profile
  // that POSITIVELY loaded with role 'inactive' gets one plain card inside the
  // bare Header and nothing else: the server refuses it every table, Storage
  // object and new login, and the resolver refuses it every released claim, so
  // no route resource may mount for it. A FAILED profile load is not this
  // state — it carries no profile and keeps the recovery shell below.
  if (profileStatus === PROFILE_STATUS.RESOLVED && isInactiveAccount(authState as AccessAuthState))
    return <InactiveAccountScreen />;

  // The released claim re-runs the resolver against the current route/profile
  // and lease before it reaches the explicit renderer, so an old claim cannot
  // paint after a Back/Forward, role, subject, or epoch transition. Any other
  // candidate is handled by the explicit transition/redirect/terminal gate
  // below; it cannot fall through to a global data boot.
  const releasedSurfaceLease = activeLegacyLeaseRef.current;
  const releasedSurfaceCandidate =
    profileStatus === PROFILE_STATUS.RESOLVED &&
    releasedSurfaceLease &&
    releasedSurfaceLease.isCurrent() &&
    sessionEpoch === releasedSurfaceLease.sessionEpoch &&
    isCurrentReleasedSurfaceClaim(activeReleasedSurfaceClaimRef.current, {
      lease: releasedSurfaceLease,
      authState,
      view,
      location,
      showForm,
      editId,
    })
      ? // N12: a released page's crash stays inside the page; App stays mounted.
        React.createElement(
          PageBoundary,
          null,
          renderReleasedAuthenticatedSurface(activeReleasedSurfaceClaimRef.current),
        )
      : null;
  const releasedClaimSurface = activeReleasedSurfaceClaimRef.current?.resolution.surface;
  const releasedSurface =
    releasedSurfaceCandidate &&
    (releasedClaimSurface === RELEASED_BROILER_BATCHES_INDEX_SURFACE ||
      releasedClaimSurface === RELEASED_BROILER_BATCHES_DETAIL_SURFACE ||
      releasedClaimSurface === RELEASED_BROILER_TIMELINE_SURFACE)
      ? React.createElement(
          ReleasedBroilerBatchesActionsProvider,
          {
            actions: {
              openAdd,
              openEdit,
              persist,
              persistDocuments,
              del,
              canDeleteAnything,
              upd,
              closeForm,
              submit,
              parseProcessorXlsx,
            },
          },
          releasedSurfaceCandidate,
        )
      : releasedSurfaceCandidate && releasedClaimSurface === RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE
        ? React.createElement(
            ReleasedBroilerBatchFormActionsProvider,
            {
              actions: {
                upd,
                closeForm,
                submit,
                del,
                openEdit,
                parseProcessorXlsx,
                persistDocuments,
              },
            },
            releasedSurfaceCandidate,
          )
        : releasedSurfaceCandidate;
  if (releasedSurface) return releasedSurface;

  const routeDisposition =
    profileStatus === PROFILE_STATUS.RESOLVED &&
    releasedSurfaceLease &&
    releasedSurfaceLease.isCurrent() &&
    sessionEpoch === releasedSurfaceLease.sessionEpoch
      ? resolveAuthenticatedRouteDisposition({
          lease: releasedSurfaceLease,
          authState,
          view,
          location,
          showForm,
          editId,
        })
      : null;

  if (profileStatus === PROFILE_STATUS.FAILED)
    return (
      <div data-farm-profile-recovery="inactive">
        {/*
         * The bounded profile retry deliberately leaves the authenticated
         * session at its least-privilege inactive role. Keep the established
         * Header recovery visible (including its retry banner), but do not
         * mount a route renderer: without a positively resolved profile, no
         * route-local resource may begin and there is no legacy all-data
         * fallback to resume.
         */}
        <Header />
      </div>
    );

  // N10: an unknown path (set by the URL->view effect) shows the Not Found
  // card under the header. Go Back resolves the nearest parent list this user
  // may open, else Home.
  if (unknownPath !== null && unknownPath === location.pathname)
    return (
      <div data-farm-route-not-found="1" style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <NotFoundCard path={location.pathname} onBack={goToParentRoute} />
      </div>
    );

  if (
    !routeDisposition ||
    routeDisposition.kind === 'released' ||
    routeDisposition.kind === 'sync-pending' ||
    routeDisposition.kind === 'redirect'
  )
    return <div data-farm-route-transition="1">Opening your workspace…</div>;

  // Light containment remains the terminal recovery for a forbidden route.
  // Handled by the terminal-recovery gate above.

  if (routeDisposition.kind === 'terminal')
    return (
      // Design Constitution N10: an in-app card under the header — 'No Access'
      // for a route this role may not open, 'We can't find that page' for
      // any other terminal route. The routing decision itself is unchanged.
      <div
        data-farm-route-terminal="1"
        data-farm-route-reason={routeDisposition.reason}
        style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      >
        <Header />
        {routeDisposition.reason === AUTHENTICATED_SURFACE_FALLBACK.ACCESS_DENIED ? (
          <NoAccessCard onBack={goToParentRoute} />
        ) : (
          <NotFoundCard path={location.pathname} onBack={goToParentRoute} />
        )}
      </div>
    );

  // Signed-in identity injected into the (now authenticated) report/form
  // surfaces as the locked submitter. Passed as a plain prop so the form
  // components keep zero auth-session coupling (no useAuth import) — the
  // public-webforms boundary guard still holds. CP1 stores the display name
  // string; durable owner identity + own-record edit/delete is a later
  // ownership/RLS checkpoint.
  const sessionSubmitter = {
    name: authState.name || authState.user?.email || '',
    email: authState.user?.email || '',
    profileId: authState.user?.id || null,
    role: authState.role || '',
  };

  // Light containment — fail closed. A Light user can only reach the portal
  // and the allowed form/Tasks surfaces; any other view renders the Light
  // portal instead of the forbidden surface (the redirect effect above also
  // snaps view/URL back to home). This blocks typed/bookmarked program
  // dashboards, the internal /fleet module, Activity, Admin, Users, and
  // Client Errors before they can mount.
  // Fail-closed Light containment. Same decision the repair effect above uses,
  // so the rendered surface and the URL state can never disagree.
  // N12: the portal sits in its own page boundary, like every routed page, so
  // a crash in it keeps the header and App mounted. The decision is unchanged.
  if (surfaceAccess.navigation === SURFACE_NAVIGATION.LIGHT_PORTAL)
    return React.createElement(PageBoundary, null, React.createElement(LightHomePortal));

  // ── REPORT/FORM SURFACES (login required) ──
  // Every authenticated user (including Light) reaches these. Logged-out
  // visitors hit the LoginScreen gate above; because the URL/view is
  // preserved across login they return to the requested form afterward.
  // B3.1 finish: the report/form surfaces own real nested routes — see
  // src/webforms/WebformsEntry.jsx. The LoginScreen gate above still guards
  // them; it is not part of this domain. That ordering is load-bearing and is
  // asserted in two halves by rls_write_boundary_plan_static and
  // prod_stability_audit_locks — the gate must precede this dispatch.
  //
  // FOUR PROPS THIS DISPATCH NO LONGER PASSES, and they are the reason
  // view.webformhub carries no residual legacy dependency. `layerGroups` and
  // `webformsConfig` were app_store documents off this boot transaction;
  // WebformHubAuthenticatedBridge reads both in the surface's own authenticated
  // scope now. `wfGroups`/`setWfGroups` were a WebformsConfigContext mirror the
  // bridge wrote and the hub read back, entirely inside that one surface. Only
  // identity and role cross this boundary. Re-adding any of them would restore
  // the exact hand-off tests/fixtures/former-eager-dependencies.json now says is
  // gone; webforms_entry_routes_static asserts their absence here and at the
  // entry.
  const webformsLocation = routedLocationFor(view, location, WEBFORMS_ROUTES);
  if (webformsLocation)
    return React.createElement(WebformsEntry, {
      location: webformsLocation,
      sessionSubmitter,
      isLight,
      role,
      // Which programs may show a daily History link. Decided here, where
      // program_access lives, so the hub never offers a link that bounces.
      dailyHistoryPrograms: visibleDailyHistoryPrograms(authState as AccessAuthState),
    });

  // ── APP-OWNED VIEWS THAT OUTRANK THE FORM ──
  // home / animalHistory / production / weather / mySubmissions sat ABOVE the
  // full-screen form branch before the route extraction, so they still do. With
  // the drawer open, a Back/Forward navigation to one of these five renders the
  // destination rather than pinning the form on screen. The remaining eleven
  // App views are dispatched below the branch, exactly as they were. See
  // src/app/appRoutes.js for the split, and app_routes_static for the proof.
  const appEntryFor = (routedLocation: ReturnType<typeof routedLocationFor>) =>
    React.createElement(AppEntry, {
      location: routedLocation,
      isLight,
    });

  const appPreFormLocation = routedLocationFor(view, location, APP_ROUTES_PRE_FORM);
  if (appPreFormLocation) return appEntryFor(appPreFormLocation);

  // ── FORM ──
  // Phase 2 Round 6 tail: body moved to src/broiler/BatchForm.tsx. Every
  // form-state piece lives in BatchesContext; every operational helper
  // stays in App() and is threaded as a prop.
  //
  // Record-page guard: when the URL is /broiler/batches/<id>, BroilerBatchPage
  // owns the render and mounts BatchForm in embedded mode. We skip the
  // full-screen branch here so the URL is the source of truth.
  // HOST GUARD (2026-08-05): the predicate is an ALLOWLIST of broiler pathnames,
  // not merely a blocklist of the record-page prefix. As a blocklist it read
  // "any pathname except /broiler/batches/<id>", which is every pathname on
  // earth, and `showForm` outlives the surface that set it: it lives in
  // BatchesProvider above the router, nothing clears it on a location change,
  // and every clearer is a click handler or a broiler-component effect. So
  // browser Back — which runs no handler at all — and the two Header
  // notification-row branches that navigate without clearing left the
  // full-screen form pinned over the destination, durably. 21 views were
  // reachable that way, and none of them ever rendered.
  //
  // This gates Step 3: until it is keyed on the URL, "this surface consumes no
  // former-eager read" is a claim about the view, not about what renders there.
  const inBroilerSpace = location.pathname === '/broiler' || location.pathname.startsWith('/broiler/');
  const notTimelineCreate = location.pathname !== '/broiler/timeline' || Boolean(editId);
  if (showForm && inBroilerSpace && notTimelineCreate && !location.pathname.startsWith('/broiler/batches/'))
    return React.createElement(BatchForm, {
      upd,
      closeForm,
      submit,
      del,
      openEdit,
      parseProcessorXlsx,
      persistDocuments,
    });

  // ── BROILER HOME DASHBOARD ──
  // B3.1 finish: the broiler domain owns real nested routes — see
  // src/broiler/BroilerEntry.jsx.
  const broilerLocation = routedLocationFor(view, location, BROILER_ROUTES);
  if (broilerLocation)
    return React.createElement(BroilerEntry, {
      location: broilerLocation,
      batches,
      refreshDailys,
      openAdd,
      openEdit,
      persist,
      persistDocuments,
      del,
      canDeleteAnything,
      upd,
      closeForm,
      submit,
      parseProcessorXlsx,
      feedOrders,
      setFeedOrders,
      poultryFeedInventory,
      setPoultryFeedInventory,
      collapsedBatches,
      setCollapsedBatches,
    });

  // B3.1 finish: the pig domain owns real nested routes — see
  // src/pig/PigEntry.jsx.
  const pigLocation = routedLocationFor(view, location, PIG_ROUTES);
  if (pigLocation)
    return React.createElement(PigEntry, {
      location: pigLocation,
      setPigDailys,
      feederGroups,
      refreshDailys,
      feedOrders,
      setFeedOrders,
      pigFeedInventory,
      setPigFeedInventory,
      pigAutoSaveTimer,
      subAutoSaveTimer,
      tripAutoSaveTimer,
      collapsedBatches,
      setCollapsedBatches,
      collapsedMonths,
      setCollapsedMonths,
    });
  // B3.1 finish: the cattle domain owns real nested routes — see
  // src/cattle/CattleEntry.jsx. routedLocationFor is both the domain predicate
  // (null = not a cattle view, keep falling through this chain) and the
  // location those nested routes match against.
  const cattleLocation = routedLocationFor(view, location, CATTLE_ROUTES);
  if (cattleLocation) return React.createElement(CattleEntry, {location: cattleLocation});

  // B3.1 finish: the sheep domain owns real nested routes — see
  // src/sheep/SheepEntry.jsx.
  const sheepLocation = routedLocationFor(view, location, SHEEP_ROUTES);
  if (sheepLocation) return React.createElement(SheepEntry, {location: sheepLocation});
  // ── PIG DAILY WEBFORM (login required) ──
  // renderWebform moved to src/webforms/PigDailysWebform.jsx; WebformsEntry
  // routes /webform-pigs through its authenticated bridge before the auth-free presenter.

  // `resolveSire` USED TO LIVE HERE: an App closure over App's OWN
  // breedingCycles and boarNames, handed to FarrowingView and SowsView as a
  // prop. It was the SECOND channel those two pages read boot values through,
  // and the one no provenance derivation can see — it scores hook destructures
  // and prop NAMES, and a closure is neither. Both pages call
  // resolveFarrowingSire themselves now, against their own route resource, and
  // both had already bound the prop as `_resolveSire` without calling it. With
  // the closure gone App reads neither value, which is why the usePig()
  // destructure above no longer takes them.

  // ── FARROWING RECORDS VIEW ──

  // ── LAYERS HOME DASHBOARD (Phase 4) ──
  // B3.1 finish: the layer domain owns real nested routes — see
  // src/layer/LayerEntry.jsx.
  const layerLocation = routedLocationFor(view, location, LAYER_ROUTES);
  if (layerLocation)
    return React.createElement(LayerEntry, {
      location: layerLocation,
      layerGroups,
    });

  // ── APP'S OWN TOP-LEVEL ROUTES ──
  // B3.1 finish: the last sixteen `view === 'X'` clauses became a route table
  // and an entry of their own — src/app/appRoutes.js and src/app/AppEntry.jsx —
  // exactly like the six domains. The seven tables partition the view space, so
  // this dispatch can sit here regardless of where the DOMAIN dispatches are;
  // the partition is asserted in tests/static/app_routes_static.test.js, not
  // assumed. What stays imperative above is what is NOT keyed on `view`: the
  // Light containment, the full-screen broiler batch form, and the login gate.
  // Position against those three IS meaningful, which is why the five views
  // that outranked the form branch are dispatched above it instead of here.
  const appLocation = routedLocationFor(view, location, APP_ROUTES_POST_FORM);
  if (appLocation) return appEntryFor(appLocation);

  // ── Cattle views ──
  // cattledailys is fully built; others are placeholder stubs until Phase 1 steps 10-13 land.
  // Unknown view - return null safely
  return null;
}

// Phase 2.1.1: WcfYN + WcfToggle moved to src/shared/ — imported at top of file.

// ── ADMIN ADD REPORT MODAL ──
// Mirrors WebformHub forms. Loads same Supabase config so Admin panel drives both.
// Phase 2.1.4: AdminAddReportModal moved to src/shared/AdminAddReportModal.jsx.
// ── BROILER DAILYS VIEW ──
// Phase 2 Round 2: BroilerDailysView moved to C:\Users\Ronni\WCF-planner\src\broiler\BroilerDailysView.tsx.

// ── LAYERS VIEW ──
// ── LAYER BATCHES VIEW ──
// Phase 2 Round 2 tail: LayerBatchesView moved to C:\Users\Ronni\WCF-planner\src\layer\LayerBatchesView.tsx.

// Phase 2 Round 3: LayersView moved to C:\Users\Ronni\WCF-planner\src\layer\LayersView.tsx.

// ── LAYER DAILYS VIEW ──
// Phase 2 Round 2: LayerDailysView moved to C:\Users\Ronni\WCF-planner\src\layer\LayerDailysView.tsx.

// ── EGG DAILYS VIEW ──
// Phase 2 Round 2: EggDailysView moved to C:\Users\Ronni\WCF-planner\src\layer\EggDailysView.tsx.

// ── PIG DAILYS VIEW ──
// Phase 2 Round 2: PigDailysView moved to C:\Users\Ronni\WCF-planner\src\pig\PigDailysView.tsx.

// ── CATTLE DAILYS VIEW ─────────────────────────────────────────────────────
// Admin list of cattle_dailys records with filters, edit, delete.
// Parallels PigDailysView / LayerDailysView pattern.
// Phase 2 Round 2: CattleDailysView moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleDailysView.tsx.

// ── CATTLE HOME DASHBOARD ───────────────────────────────────────────────────
// Stats tiles + per-herd live weight / cow units / mortality.
// Phase 2 Round 3: CattleHomeView moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleHomeView.tsx.

// ── CATTLE BULK IMPORT ─────────────────────────────────────────────────────
// Self-serve cattle uploader. User downloads a fixed-shape xlsx template,
// fills it (or has Claude pre-seed it), uploads it back, previews per-row,
// and commits. Per row: inserts a cattle row, plus optional calving record
// (last_calve_date), optional comment (comment), optional receiving weigh-in
// (receiving_weight). Auto-creates new breeds/origins as needed.
// Phase 2 Round 2: CattleBulkImport moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleBulkImport.tsx.

// ── SHEEP BULK IMPORT ──────────────────────────────────────────────────────
// Same shape as CattleBulkImport but adapted to sheep schema (no pct_wagyu,
// flock instead of herd, lambing instead of calving, sex enum is
// ewe/ram/wether). Auto-creates new breeds/origins on commit.
// Phase 2 Round 2: SheepBulkImport moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepBulkImport.tsx.

// ── SHEEP DETAIL PANEL ─────────────────────────────────────────────────────
// Inline expansion under a sheep row. Slimmer than CowDetail — Phase 1 keeps
// the essentials: identity, lineage tags, weight history table, lambing
// history, comments timeline. Charts + nav stack are Phase 2.
// Phase 2 Round 2: SheepDetail moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepDetail.tsx.

// ── SHEEP FLOCKS VIEW ──────────────────────────────────────────────────────
// Mirror of CattleHerdsView with sheep terminology (flock/ewe/ram/wether,
// lambing instead of calving). Phase 1 covers: directory + flat/tile modes,
// add/edit/delete, transfer, inline detail, bulk import.
// Phase 2 Round 3: SheepFlocksView moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepFlocksView.jsx.

// ── SHEEP HOME DASHBOARD ───────────────────────────────────────────────────
// Top stats + per-flock count tiles + rolling-window card with sheep-specific
// MetricsGrid (bales of hay, alfalfa lbs, mineral compliance %, fence
// voltage avg, mortality, report days). Period toggle 30/90/120 days.
// Phase 1 has no nutrition targets, so target % cells are skipped.
// Phase 2 Round 3: SheepHomeView moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepHomeView.jsx.

// ── SHEEP DAILYS VIEW (admin entry) ────────────────────────────────────────
// Add/edit daily reports for sheep. Sheep-specific fields: bales of hay,
// alfalfa lbs, minerals given + % eaten, fence voltage kV, waterers working.
// Phase 2 Round 2: SheepDailysView moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepDailysView.jsx.

// ── SHEEP WEIGH-INS VIEW (admin) ───────────────────────────────────────────
// Session-based weigh-ins on the shared weigh_in_sessions / weigh_ins tables
// with species='sheep'. Phase 1: list sessions, expand to entries, create
// session, add/edit/delete entries. Retag flow deferred to Phase 2.
// Phase 2 Round 2: SheepWeighInsView moved to C:\Users\Ronni\WCF-planner\src\sheep\SheepWeighInsView.jsx.

// ── CATTLE HERDS VIEW (merged with Directory) ──────────────────────────────
// Single tab combining per-herd tiles + global search/filter across all cattle.
// Add/edit/transfer/delete happen here. Cow detail expands inline with weigh
// history, calving history (Mommas), comments timeline.
// Phase 2 Round 3: CattleHerdsView moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleHerdsView.tsx.

// Helper component — outcome (Processed/Deceased/Sold) sections collapsed at bottom of Herds view
// Phase 2 Round 2: CollapsibleOutcomeSections moved to C:\Users\Ronni\WCF-planner\src\cattle\CollapsibleOutcomeSections.jsx.

// Helper component — cow detail body shown when expanded inside the herd tile
// Phase 2 Round 2: CowDetail moved to C:\Users\Ronni\WCF-planner\src\cattle\CowDetail.tsx.

// ── CATTLE BREEDING VIEW ────────────────────────────────────────────────────
// Lists breeding cycles with computed dates, status, outstanding cows.
// Phase 2 Round 3: CattleBreedingView moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleBreedingView.tsx.

// ── CATTLE PROCESSING BATCHES VIEW ─────────────────────────────────────────
// Lists processing batches (C-26-01 etc.) with cow lists and yield data.
// Phase 2 Round 3: CattleBatchesView moved to C:\Users\Ronni\WCF-planner\src\cattle\CattleBatchesView.tsx.

// ── CATTLE WEIGH-INS VIEW ───────────────────────────────────────────────────
// Authenticated review of past weigh-in sessions. Drill into a session to see
// individual entries. Highlights new-tag flags for admin reconciliation.
// Modal that creates a new draft weigh_in_sessions row from inside the admin
// LivestockWeighInsView (broiler + pig only). Mirrors the AdminAddReportModal
// pattern -- no navigate-away. After insert, the parent loadAll-then-expand
// hook drops the user straight into the in-tile grid for that fresh session.
// Phase 2.1.4: AdminNewWeighInModal moved to C:\Users\Ronni\WCF-planner\src\shared\AdminNewWeighInModal.jsx.

// Modal for "Send to Trip" — pick a feeder group + existing trip or create new.
// Totally stateless wrt Supabase; just collects inputs and hands them back.
// Phase 2.1.4: PigSendToTripModal moved to C:\Users\Ronni\WCF-planner\src\livestock\PigSendToTripModal.tsx.

// Modal: start a new cattle weigh-in session inline (no webform nav).
// Phase 2.1.4: CattleNewWeighInModal moved to src/cattle/CattleNewWeighInModal.jsx.

// === END VERBATIM PORT ===

export default App;
